import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { IoRefresh, IoPersonAdd, IoDocumentText, IoCard, IoCheckmark, IoArrowForward, IoArrowBack } from 'react-icons/io5';
import {
  getAppliedPaymentCandidates,
  AppliedPaymentCandidate,
  fetchAppliedPaymentsQboDetails,
  AppliedPaymentsQboDetails,
  AppliedPaymentApplication,
  applyPaymentToInvoice,
  ApplyPaymentRequest,
  syncInvoiceToQbo,
  pingQboCompanyLegacy,
  QboConnectionStatus,
  syncPaymentForReconciliation,
  syncInvoiceForReconciliation,
  syncInvoicesForReconciliation,
  syncCustomerForReconciliation,
  getAppliedPaymentsSummary,
  getQboTokenInfo,
} from 'services/reconciliationService';
import { DevTab } from './DevTab';

// Debug flag - set to false to disable debug logging
const DEBUG_LOGGING = true;

// Helper function for digits-only comparison
const digitsOnly = (x?: string | number) => (x ?? '').toString().replace(/\D+/g, '');

type PaymentApplicationSummary = {
  app: AppliedPaymentApplication;
  invoiceQboId: string | null;
  qboInvoice: any;
  qboInvoiceExists: boolean;
  qboInvoiceDeleted: boolean;
  expectedAppliedCents: number;
  qboAppliedCents: number | null;
  invoiceAppliedCorrectly: boolean;
  invoiceStatusClass: string;
  shouldShowResync: boolean;
  applyDisabledReason: 'invoice_unsynced' | 'payment_unsynced' | 'already_applied' | 'cannot_correct' | null;
  shouldShowApplyButton: boolean;
  shouldShowApply: boolean;
};

const extractQboFaultMessage = (error: any): string | null => {
  if (!error) return null;

  const faultErrors = error?.Fault?.Error;
  if (Array.isArray(faultErrors) && faultErrors.length > 0) {
    return faultErrors
      .map((err: any) => [err.Message, err.Detail].filter(Boolean).join(' - '))
      .filter(Boolean)
      .join('; ');
  }

  const nested = error?.response?.data;
  if (nested) {
    const nestedMessage = extractQboFaultMessage(nested);
    if (nestedMessage) return nestedMessage;
  }

  if (typeof error?.message === 'string') return error.message;
  if (typeof error?.Detail === 'string') return error.Detail;
  if (typeof error === 'string') return error;

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const stringifyError = (value: any): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

// Removed unused constants

const buildPaymentApplications = (
  payment: AppliedPaymentCandidate | null,
  qboData: any
): PaymentApplicationSummary[] => {
  console.log('🚨 BUILD PAYMENT APPLICATIONS CALLED:', {
    paymentId: payment?.payment_id,
    applicationsCount: payment?.applications?.length || 0,
    qboData: qboData ? { payments: qboData.payments?.length || 0, invoices: qboData.invoices?.length || 0 } : null
  });
  
  if (!payment) return [];

  const qboPayments = Array.isArray(qboData?.payments) ? qboData.payments : [];
  const qboInvoices = Array.isArray(qboData?.invoices) ? qboData.invoices : [];

  const paymentEntry = payment.payment_qbo_id
    ? qboPayments.find((p: any) => String(p.id) === String(payment.payment_qbo_id))
    : null;
  const qboPaymentData = paymentEntry?.success ? paymentEntry.data : null;
  const paymentSynced = Boolean(payment.payment_qbo_id);

  const amountsByInvoice = new Map<string, number>();
  if (qboPaymentData?.Line) {
    (qboPaymentData.Line as any[]).forEach((line: any) => {
      if (!line) return;
      const rawAmount = Number(line.Amount ?? 0);
      if (Number.isNaN(rawAmount)) return;
      const amountCents = Math.round(rawAmount * 100);
      const linked = Array.isArray(line.LinkedTxn) ? line.LinkedTxn : [];
      linked.forEach((txn: any) => {
        if (txn?.TxnType === 'Invoice' && txn?.TxnId != null) {
          const key = String(txn.TxnId);
          amountsByInvoice.set(key, (amountsByInvoice.get(key) ?? 0) + amountCents);
        }
      });
    });
  }

  const invoiceEntries = new Map<string, any>();
  qboInvoices.forEach((inv: any) => {
    if (!inv) return;
    const keys: string[] = [];
    if (inv.id != null) {
      keys.push(String(inv.id));
    }
    if (inv?.data?.Id != null) {
      keys.push(String(inv.data.Id));
    }
    const digitsId = digitsOnly(inv?.data?.Id ?? inv?.id);
    if (digitsId) {
      keys.push(digitsId);
    }

    keys.forEach(key => {
      if (!key) return;
      if (!invoiceEntries.has(key)) {
        invoiceEntries.set(key, inv);
      }
    });
  });

  return (payment.applications || []).map(app => {
    const invoiceQboId = app.invoice_qbo_id ? String(app.invoice_qbo_id) : null;
    const invoiceEntry = invoiceQboId
      ? invoiceEntries.get(invoiceQboId) || invoiceEntries.get(digitsOnly(invoiceQboId))
      : null;
    const invoiceData = invoiceEntry?.success ? invoiceEntry.data : null;
    const invoiceExists = Boolean(invoiceData);
    const invoiceDeleted = Boolean(invoiceData?.SyncToken === '0' || invoiceData?.Deleted === true);
    const invoiceSynced = Boolean(invoiceQboId);
    
    if (DEBUG_LOGGING) console.log('DEBUG - Invoice processing:', {
      invoiceId: app.invoice_id,
      invoiceQboId,
      invoiceEntry: invoiceEntry ? { success: invoiceEntry.success, hasData: !!invoiceEntry.data } : null,
      invoiceExists,
      invoiceDeleted,
      invoiceEntriesKeys: Array.from(invoiceEntries.keys())
    });
    const expectedAppliedCents = Math.round(Number(app.applied_amount ?? 0));
    const qboAppliedCents = invoiceQboId != null && amountsByInvoice.has(invoiceQboId)
      ? amountsByInvoice.get(invoiceQboId) ?? null
      : null;
    const invoiceAppliedCorrectly = invoiceQboId
      ? qboAppliedCents != null && qboAppliedCents === expectedAppliedCents
      : expectedAppliedCents === 0;
    const applyDisabledReason = app.can_correct === false
      ? 'cannot_correct'
      : !invoiceSynced
        ? 'invoice_unsynced'
        : !paymentSynced
          ? 'payment_unsynced'
          : invoiceAppliedCorrectly
            ? 'already_applied'
            : null;
    const shouldShowApplyButton = true;
    const shouldShowApply = Boolean(app.can_correct !== false && invoiceSynced && paymentSynced && !invoiceAppliedCorrectly);

    console.log('DEBUG - Invoice processing:', {
      invoiceId: app.invoice_id,
      invoiceQboId,
      rawInvoiceQboId: app.invoice_qbo_id,
      invoiceEntry: invoiceEntry ? { success: invoiceEntry.success, hasData: !!invoiceEntry.data } : null,
      invoiceExists,
      invoiceDeleted,
      invoiceEntriesKeys: Array.from(invoiceEntries.keys()),
      shouldShowApplyButton,
      shouldShowApply
    });
    const invoiceStatusClass = !invoiceQboId
      ? 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs'
      : (!invoiceExists || invoiceDeleted)
        ? 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs'
        : invoiceAppliedCorrectly
          ? 'bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs'
          : 'bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-xs';
    const shouldShowResync = Boolean(invoiceQboId && (!invoiceExists || invoiceDeleted));
    

    return {
      app,
      invoiceQboId,
      qboInvoice: invoiceEntry,
      qboInvoiceExists: invoiceExists,
      qboInvoiceDeleted: invoiceDeleted,
      expectedAppliedCents,
      qboAppliedCents,
      invoiceAppliedCorrectly,
      invoiceStatusClass,
      shouldShowResync,
      applyDisabledReason,
      shouldShowApplyButton,
      shouldShowApply
    };
  });
};

const fetchPaymentQboContext = async (
  payment: AppliedPaymentCandidate | null,
  fetcher: (items: AppliedPaymentCandidate[]) => Promise<AppliedPaymentsQboDetails | null>,
  fallback: any
) => {
  if (!payment) return null;
  const snapshot = await fetcher([payment]);
  if (snapshot) {
    return snapshot;
  }
  return fallback;
};

const evaluatePaymentForFix = (
  payment: AppliedPaymentCandidate | null,
  context: any
) => {
  if (!payment) {
    return {
      customerNeedsSync: false,
      paymentNeedsSync: false,
      invoiceIdsToSync: [] as number[],
      applyTargets: [] as PaymentApplicationSummary[],
      applicationSummaries: [] as PaymentApplicationSummary[],
    };
  }

  const hasContextData = context && (
    Array.isArray(context.payments) ||
    Array.isArray(context.customers) ||
    Array.isArray(context.invoices)
  );

  const qboPayments = hasContextData && Array.isArray(context.payments) ? context.payments : [];
  const qboCustomers = hasContextData && Array.isArray(context.customers) ? context.customers : [];

  const paymentEntry = payment.payment_qbo_id
    ? qboPayments.find((p: any) => String(p.id) === String(payment.payment_qbo_id))
    : null;
  const paymentExists = hasContextData
    ? Boolean(paymentEntry?.success && paymentEntry?.data)
    : Boolean(payment.payment_qbo_id);

  const customerEntry = payment.customer_qbo_id
    ? qboCustomers.find((c: any) => String(c.id) === String(payment.customer_qbo_id))
    : null;
  const customerExists = hasContextData
    ? Boolean(customerEntry?.success && customerEntry?.data)
    : Boolean(payment.customer_qbo_id);

  const applicationSummaries = buildPaymentApplications(payment, context);

  const invoiceIdsToSync = (payment.applications || [])
    .filter(app => !app.invoice_qbo_id)
    .map(app => app.invoice_id)
    .filter(id => Number.isFinite(id));

  const applyTargets = (() => {
    const seen = new Set<string>();
    const targets: PaymentApplicationSummary[] = [];
    applicationSummaries.forEach(summary => {
      if (!summary.shouldShowApply || !summary.invoiceQboId || summary.applyDisabledReason) return;
      const key = `${summary.app.invoice_id}-${summary.invoiceQboId}`;
      if (!seen.has(key)) {
        seen.add(key);
        targets.push(summary);
      }
    });
    return targets;
  })();

  return {
    customerNeedsSync: !payment.customer_qbo_id || !customerExists,
    paymentNeedsSync: !payment.payment_qbo_id || !paymentExists,
    invoiceIdsToSync,
    applyTargets,
    applicationSummaries,
  };
};

// Component to display multiple invoice IDs with hover functionality
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceIdsDisplay: React.FC<{ applications: AppliedPaymentApplication[] }> = ({ applications }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!applications || applications.length === 0) {
    return <span>—</span>;
  }

  if (applications.length === 1) {
    const app = applications[0];
    return (
      <span>
        {app.invoice_id} <span className="text-xs text-gray-500">(QBO {app.invoice_qbo_id ?? '—'})</span>
      </span>
    );
  }

  const firstApp = applications[0];
  const remainingCount = applications.length - 1;

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className="cursor-pointer text-xs">
        {firstApp.invoice_id} <span className="text-xs text-gray-500">(QBO {firstApp.invoice_qbo_id ?? '—'})</span>
        <span className="text-xs text-blue-600 ml-1">(+{remainingCount} more)</span>
      </span>
      
      {isHovered && (
        <div className="absolute z-10 mt-1 left-0 bg-white border border-gray-300 rounded-md shadow-lg p-2 min-w-max">
          <div className="text-xs font-medium text-gray-700 mb-1">All Invoice IDs:</div>
          <div className="space-y-1">
            {applications.map((app, index) => (
              <div key={index} className="text-xs text-gray-800">
                {app.invoice_id} <span className="text-gray-500">(QBO {app.invoice_qbo_id ?? '—'})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Connection status indicator component
const ConnectionIndicator: React.FC<{ status: 'checking' | 'connected' | 'disconnected' | 'error' }> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'checking':
        return {
          icon: (
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
          ),
          text: 'Checking...',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      case 'connected':
        return {
          icon: (
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          ),
          text: 'Connected',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'disconnected':
        return {
          icon: (
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
          ),
          text: 'Disconnected',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
      case 'error':
        return {
          icon: (
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          ),
          text: 'Error',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      default:
        return {
          icon: (
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
          ),
          text: 'Unknown',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={`inline-flex items-center gap-1.5 px-1 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.borderColor} ${config.color}`}>
      {config.icon}
      <span>{config.text}</span>
    </div>
  );
};

const AppliedPaymentsScanModal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'investigate' | 'crawl' | 'dev'>('dev');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AppliedPaymentCandidate[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [qbo, setQbo] = useState<AppliedPaymentsQboDetails | null>(null);
  const [qboLoading, setQboLoading] = useState(false);
  const [fixingIds, setFixingIds] = useState<Set<number>>(new Set());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fixResult, setFixResult] = useState<Record<number, { ok: boolean; message: string }>>({});
  const [banner, setBanner] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [syncingInvoices, setSyncingInvoices] = useState<Set<number>>(new Set());
  const [applyingPayments, setApplyingPayments] = useState<Set<string>>(new Set());
  const [resyncingPayments, setResyncingPayments] = useState<Set<number>>(new Set());
  const [resyncingCustomers, setResyncingCustomers] = useState<Set<number>>(new Set());
  const [customerStatuses, setCustomerStatuses] = useState<Record<number, { type: 'success' | 'error' | 'info'; text: string }>>({});
  const [connectionStatus, setConnectionStatus] = useState<QboConnectionStatus | null>(null);
  const [recentlySyncedInvoices, setRecentlySyncedInvoices] = useState<Set<number>>(new Set());

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showMissingInQbo, setShowMissingInQbo] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);
  
  // Page size options
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Summary data state
  const [summaryData, setSummaryData] = useState<{
    local: {
      invoicesToScan: number;
      mismatches: number;
      lastRun: string | null;
    };
    qbo: {
      totalInvoices: number;
      paidInFull: number;
      totalPayments: number;
      totalAmount: number;
    } | null;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Crawl tool states
  const [crawlTotals, setCrawlTotals] = useState<{
    eserv: {
      totalInvoices: number;
      paidInvoices: number;
      unpaidInvoices: number;
      totalPayments: number;
      totalAmount: number;
      totalCustomers: number;
    } | null;
    qbo: {
      totalInvoices: number;
      paidInvoices: number;
      unpaidInvoices: number;
      totalPayments: number;
      totalAmount: number;
      totalCustomers: number;
      note?: string;
    } | null;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [crawlStarted, setCrawlStarted] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<AppliedPaymentCandidate | null>(null);
  const [crawlProgress, setCrawlProgress] = useState<{
    current: number;
    total: number;
    processed: number;
    reconciled: number;
    needsFix: number;
  } | null>(null);
  const [crawlStatus, setCrawlStatus] = useState<'idle' | 'fetching_totals' | 'ready' | 'crawling' | 'paused' | 'completed'>('idle');
  const [crawlUiState, setCrawlUiState] = useState<'idle' | 'loading' | 'matched' | 'ready'>('idle');
  const [crawlAutoAdvanceTimer, setCrawlAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [crawlAdvanceCountdown, setCrawlAdvanceCountdown] = useState<number | null>(null);
  const [isAutoAdvancePaused, setIsAutoAdvancePaused] = useState(false);
  const [stopOnMismatch, setStopOnMismatch] = useState(true); // Stop advancing when mismatch encountered
  const [qboConnectionStatus, setQboConnectionStatus] = useState<'checking' | 'connected' | 'disconnected' | 'error'>('checking');
  const [qboTokenInfo, setQboTokenInfo] = useState<any>(null);
  const [qboTotals, setQboTotals] = useState<any>(null);
  const [eservTotals, setEservTotals] = useState<any>(null);
  const [paymentSearchId, setPaymentSearchId] = useState<string>('');
  const [searchedPayment, setSearchedPayment] = useState<any>(null);
  const [customerSearchId, setCustomerSearchId] = useState<string>('');
  const [searchedCustomer, setSearchedCustomer] = useState<any>(null);
  const [invoiceSearchId, setInvoiceSearchId] = useState<string>('');
  const [searchedInvoice, setSearchedInvoice] = useState<any>(null);
  const [isDataComparisonCollapsed, setIsDataComparisonCollapsed] = useState(false);
  const [startPaymentId, setStartPaymentId] = useState<string>('');
  const [syncingItems, setSyncingItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const crawlStatusRef = useRef(crawlStatus);
  const currentPaymentRef = useRef<AppliedPaymentCandidate | null>(currentPayment);
  const crawlAutoAdvanceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processedCountRef = useRef(0); // Track how many payments we've actually processed


  const clearCrawlAutoAdvance = () => {
    if (DEBUG_LOGGING) console.log('clearCrawlAutoAdvance called, clearing interval:', crawlAutoAdvanceIntervalRef.current);
    console.trace('clearCrawlAutoAdvance call stack');
    setCrawlAutoAdvanceTimer(prev => {
      if (prev) {
        clearTimeout(prev);
      }
      return null;
    });
    if (crawlAutoAdvanceIntervalRef.current) {
      if (DEBUG_LOGGING) console.log('Clearing interval:', crawlAutoAdvanceIntervalRef.current);
      clearInterval(crawlAutoAdvanceIntervalRef.current);
      crawlAutoAdvanceIntervalRef.current = null;
    }
    setCrawlAdvanceCountdown(null);
  };

  useEffect(() => {
    crawlStatusRef.current = crawlStatus;
  }, [crawlStatus]);

  useEffect(() => {
    currentPaymentRef.current = currentPayment;
  }, [currentPayment]);

  // Debug crawlProgress changes
  useEffect(() => {
    if (DEBUG_LOGGING) console.log('📊 crawlProgress changed:', crawlProgress);
  }, [crawlProgress]);

  useEffect(() => {
    return () => {
      if (crawlAutoAdvanceTimer) {
        clearTimeout(crawlAutoAdvanceTimer);
      }
      if (crawlAutoAdvanceIntervalRef.current) {
        clearInterval(crawlAutoAdvanceIntervalRef.current);
        crawlAutoAdvanceIntervalRef.current = null;
      }
    };
  }, [crawlAutoAdvanceTimer]);

  useEffect(() => {
    if (DEBUG_LOGGING) console.log('useEffect triggered - crawlStatus:', crawlStatus, 'crawlUiState:', crawlUiState);
    if (crawlStatus !== 'crawling') {
      if (DEBUG_LOGGING) console.log('Crawl not active, clearing auto-advance');
      clearCrawlAutoAdvance();
      if (crawlStatus === 'idle') {
        setCrawlUiState('idle');
      } else if (crawlUiState === 'loading' || crawlUiState === 'matched') {
        setCrawlUiState('ready');
      }
    }
  }, [crawlStatus, crawlUiState]);

  // Fetch QBO data when currentPayment changes
  useEffect(() => {
    if (currentPayment) {
      console.log('🔍 Current payment changed, fetching QBO data:', currentPayment.payment_id);
      console.log('🔍 Current payment customer_qbo_id:', currentPayment.customer_qbo_id);
      
      // Force fetch QBO data including customer data
      const fetchData = async () => {
        try {
          const qboData = await fetchQboDataForItems([currentPayment]);
          console.log('🔍 QBO data fetched for current payment:', !!qboData);
          console.log('🔍 QBO data customers array:', qboData?.customers?.length || 0);
          if (qboData) {
            setQbo(qboData);
          }
        } catch (error) {
          console.error('🔍 Failed to fetch QBO data for current payment:', error);
        }
      };
      
      fetchData();
    }
  }, [currentPayment]);
  
  const qboPayload = useMemo(() => {
    if (!qbo) return null;
    const payload = (qbo as any)?.data ? (qbo as any).data : qbo;
    if (DEBUG_LOGGING) console.log('DEBUG - QBO payload structure:', {
      qbo: qbo,
      payload: payload,
      customers: payload?.customers,
      customerCount: payload?.customers?.length || 0,
      payments: payload?.payments,
      paymentCount: payload?.payments?.length || 0
    });
    return payload;
  }, [qbo]);

  const currentPaymentQboEntry = useMemo(() => {
    if (!currentPayment?.payment_qbo_id || !qboPayload?.payments) {
      if (DEBUG_LOGGING) console.log('DEBUG - No payment QBO ID or no payments data:', {
        paymentQboId: currentPayment?.payment_qbo_id,
        hasPayments: !!qboPayload?.payments
      });
      return null;
    }

    const targetId = String(currentPayment.payment_qbo_id).trim();
    const found = qboPayload.payments.find((p: any) => String(p.id).trim() === targetId) || null;
    if (DEBUG_LOGGING) console.log('DEBUG - Payment lookup result:', {
      lookingFor: targetId,
      found: found ? { id: found.id, success: found.success, hasData: !!found.data } : null,
      allPaymentIds: qboPayload.payments.map((p: any) => ({ id: p.id, success: p.success }))
    });
    return found;
  }, [currentPayment, qboPayload]);

  const currentPaymentQboData = currentPaymentQboEntry?.success ? currentPaymentQboEntry.data : null;
  const normalizedLocalPaymentQboId = useMemo(() => {
    if (!currentPayment?.payment_qbo_id) return null;
    return String(currentPayment.payment_qbo_id).trim();
  }, [currentPayment]);
  const normalizedRemotePaymentQboId = useMemo(() => {
    if (!currentPaymentQboData?.Id) return null;
    return String(currentPaymentQboData.Id).trim();
  }, [currentPaymentQboData]);
  const currentPaymentIdsMatch = useMemo(() => {
    if (!normalizedLocalPaymentQboId || !normalizedRemotePaymentQboId) {
      return false;
    }

    if (normalizedLocalPaymentQboId === normalizedRemotePaymentQboId) {
      return true;
    }

    try {
      return BigInt(normalizedLocalPaymentQboId) === BigInt(normalizedRemotePaymentQboId);
    } catch (error) {
      return false;
    }
  }, [normalizedLocalPaymentQboId, normalizedRemotePaymentQboId]);

  const currentPaymentQboAmountCents = currentPaymentQboData?.TotalAmt != null
    ? Math.round(Number(currentPaymentQboData.TotalAmt) * 100)
    : null;
  const currentPaymentQboCustomerId: string | null = currentPaymentQboData?.CustomerRef?.value
    ? String(currentPaymentQboData.CustomerRef.value)
    : currentPayment?.customer_qbo_id
    ? String(currentPayment.customer_qbo_id)
    : null;

  if (DEBUG_LOGGING) console.log('DEBUG - currentPaymentQboCustomerId calculation:', {
    currentPaymentQboData: currentPaymentQboData ? {
      Id: currentPaymentQboData.Id,
      CustomerRef: currentPaymentQboData.CustomerRef
    } : null,
    currentPayment: currentPayment ? {
      payment_id: currentPayment.payment_id,
      customer_qbo_id: currentPayment.customer_qbo_id
    } : null,
    currentPaymentQboCustomerId,
    source: currentPaymentQboData?.CustomerRef?.value ? 'qbo_payment_data' : 'eserv_data'
  });

  // Debug logging for current payment data
  if (DEBUG_LOGGING) console.log('DEBUG - Current payment data:', {
    currentPayment: currentPayment ? {
      payment_id: currentPayment.payment_id,
      customer_qbo_id: currentPayment.customer_qbo_id,
      fk_bcustomer_id: currentPayment.fk_bcustomer_id,
      company_name: currentPayment.company_name,
      first_name: currentPayment.first_name,
      last_name: currentPayment.last_name
    } : null,
    currentPaymentQboData: currentPaymentQboData ? {
      Id: currentPaymentQboData.Id,
      CustomerRef: currentPaymentQboData.CustomerRef,
      TotalAmt: currentPaymentQboData.TotalAmt
    } : null,
    currentPaymentQboCustomerId
  });

  if (DEBUG_LOGGING) console.log('DEBUG - QBO state:', {
    qbo,
    customers: qbo?.customers,
    customerCount: qbo?.customers?.length,
    hasQboData: !!qbo,
    hasCustomers: !!(qbo?.customers && qbo.customers.length > 0)
  });

  if (DEBUG_LOGGING) console.log('DEBUG - Full QBO object:', JSON.stringify(qbo, null, 2));
  const currentPaymentQboDate: string | null = currentPaymentQboData?.TxnDate
    ? String(currentPaymentQboData.TxnDate)
    : (currentPaymentQboData?.MetaData?.LastUpdatedTime ? String(currentPaymentQboData.MetaData.LastUpdatedTime) : null);
  const currentPaymentQboCustomerEntry = useMemo(() => {
    if (DEBUG_LOGGING) console.log('DEBUG - currentPaymentQboCustomerEntry calculation:', {
      currentPaymentQboCustomerId,
      customers: qbo?.customers,
      customerCount: qbo?.customers?.length || 0
    });
    
    if (!currentPaymentQboCustomerId || !qbo?.customers) {
      if (DEBUG_LOGGING) console.log('DEBUG - No customer ID or no QBO customers data');
      return null;
    }

    const found = qbo?.customers?.find((c: any) => String(c.id) === String(currentPaymentQboCustomerId)) || null;
    if (DEBUG_LOGGING) console.log('DEBUG - Customer lookup result:', {
      lookingFor: currentPaymentQboCustomerId,
      lookingForType: typeof currentPaymentQboCustomerId,
      customersArray: qbo?.customers?.map((c: any) => ({
        id: c.id,
        idType: typeof c.id,
        success: c.success,
        hasData: !!c.data,
        displayName: c.data?.DisplayName
      })) || [],
      found: found ? { id: found.id, success: found.success, hasData: !!found.data } : null,
      allCustomerIds: qbo?.customers?.map((c: any) => ({ id: c.id, success: c.success })) || [],
      comparisonTest: qbo?.customers?.map((c: any) => ({
        id: c.id,
        stringEqual: String(c.id) === String(currentPaymentQboCustomerId),
        strictEqual: c.id === currentPaymentQboCustomerId
      })) || []
    });
    
    return found;
  }, [currentPaymentQboCustomerId, qbo]);
  const currentPaymentQboCustomerExists = Boolean(currentPaymentQboCustomerEntry?.success && currentPaymentQboCustomerEntry.data);
  
  if (DEBUG_LOGGING) console.log('DEBUG - Customer exists check:', {
    currentPaymentQboCustomerEntry: currentPaymentQboCustomerEntry,
    hasSuccess: currentPaymentQboCustomerEntry?.success,
    hasData: !!currentPaymentQboCustomerEntry?.data,
    currentPaymentQboCustomerExists: currentPaymentQboCustomerExists
  });
  
  const currentPaymentQboCustomerName: string | null = currentPaymentQboCustomerEntry?.data?.DisplayName
    ? String(currentPaymentQboCustomerEntry.data.DisplayName)
    : currentPaymentQboCustomerEntry?.data?.CompanyName
    ? String(currentPaymentQboCustomerEntry.data.CompanyName)
    : currentPaymentQboCustomerEntry?.data?.FullyQualifiedName
    ? String(currentPaymentQboCustomerEntry.data.FullyQualifiedName)
    : currentPaymentQboData?.CustomerRef?.name
    ? String(currentPaymentQboData.CustomerRef.name)
    : '—';

  if (DEBUG_LOGGING) console.log('DEBUG - Customer name calculation:', {
    currentPaymentQboData: currentPaymentQboData?.CustomerRef,
    currentPaymentQboCustomerEntry: currentPaymentQboCustomerEntry?.data,
    currentPaymentQboCustomerName,
    currentPaymentQboCustomerId
  });
  
  // Parse QBO ID from EServ display string (e.g., "192492 / QBO: 84" -> "84")
  if (DEBUG_LOGGING) console.log('DEBUG - EServ customer_qbo_id raw value:', {
    rawValue: currentPayment?.customer_qbo_id,
    rawType: typeof currentPayment?.customer_qbo_id,
    stringValue: String(currentPayment?.customer_qbo_id || ''),
    includesQBO: String(currentPayment?.customer_qbo_id || '').includes('QBO:')
  });
  
  const eservCustomerQboId = currentPayment?.customer_qbo_id 
    ? String(currentPayment.customer_qbo_id).includes('QBO:')
      ? String(currentPayment.customer_qbo_id).split('QBO:')[1]?.trim() || null
      : String(currentPayment.customer_qbo_id)
    : null;
    
  if (DEBUG_LOGGING) console.log('DEBUG - EServ customer ID after parsing:', {
    eservCustomerQboId,
    eservType: typeof eservCustomerQboId,
    eservLength: eservCustomerQboId?.length
  });
  
  const qboCustomerIdFromData = currentPaymentQboCustomerEntry?.data?.Id ? String(currentPaymentQboCustomerEntry.data.Id) : null;
  
  if (DEBUG_LOGGING) console.log('DEBUG - QBO customer ID from data:', {
    currentPaymentQboCustomerEntry: currentPaymentQboCustomerEntry,
    hasData: !!currentPaymentQboCustomerEntry?.data,
    dataId: currentPaymentQboCustomerEntry?.data?.Id,
    dataIdType: typeof currentPaymentQboCustomerEntry?.data?.Id,
    qboCustomerIdFromData,
    qboType: typeof qboCustomerIdFromData,
    qboLength: qboCustomerIdFromData?.length
  });
  
  // Only use QBO customer ID from actual QBO customer data - NO FALLBACKS
  const actualQboCustomerId = qboCustomerIdFromData;
  
  // 4-state customer comparison: match_active, match_inactive, missing, mismatch
  const getCustomerMatchState = () => {
    if (!currentPayment || !qbo?.customers || qbo.customers.length === 0 || !eservCustomerQboId) {
      return 'missing';
    }
    
    const qboCustomer = qbo.customers.find((c: any) => 
      c.success && c.data && digitsOnly(c.data.Id) === digitsOnly(eservCustomerQboId)
    );
    
    if (!qboCustomer) {
      return 'missing';
    }
    
    // Check for both inactive and deleted customers
    if (qboCustomer.isActive === false || qboCustomer.data.Active === false) {
      return 'match_inactive';
    }
    
    return 'match_active';
  };
  
  const customerMatchState = getCustomerMatchState();
  const customerIdsMatch = customerMatchState === 'match_active';
  
  if (currentPayment) {
    if (DEBUG_LOGGING) console.log('DEBUG - Customer ID comparison:', {
      eservCustomerQboId,
      qboCustomerIdFromData,
      actualQboCustomerId,
      customerIdsMatch,
      eservCustomerQboIdType: typeof eservCustomerQboId,
      actualQboCustomerIdType: typeof actualQboCustomerId,
      currentPaymentQboCustomerEntry: currentPaymentQboCustomerEntry,
      customersArray: qbo?.customers,
      currentPaymentQboCustomerExists: currentPaymentQboCustomerExists
    });
  }
  
  if (currentPayment) {
    if (DEBUG_LOGGING) console.log('DEBUG - EXACT COMPARISON VALUES:', {
      eservCustomerQboId: `"${eservCustomerQboId}"`,
      qboCustomerIdFromData: `"${qboCustomerIdFromData}"`,
      actualQboCustomerId: `"${actualQboCustomerId}"`,
      eservLength: eservCustomerQboId?.length,
      qboLength: qboCustomerIdFromData?.length,
      actualLength: actualQboCustomerId?.length,
      areEqual: eservCustomerQboId === actualQboCustomerId,
      stringEqual: String(eservCustomerQboId) === String(actualQboCustomerId),
      customerIdsMatch: customerIdsMatch
    });
  }
  
  // 4-state customer badge system
  const getCustomerBadgeClass = (state: string) => {
    switch (state) {
      case 'match_active':
        return 'bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs';
      case 'match_inactive':
        return 'bg-amber-100 text-amber-800 px-1 py-0.5 rounded text-xs';
      case 'missing':
      case 'mismatch':
      default:
        return 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs';
    }
  };
  
  const currentPaymentQboCustomerBadgeClass = currentPayment && 
      qbo?.customers && 
      qbo.customers.length > 0
    ? getCustomerBadgeClass(customerMatchState)
    : 'bg-red-100 text-red-800 px-2 py-1 rounded text-xs';

  // 4-state invoice comparison logic
  const getInvoiceMatchState = () => {
    if (!currentPayment || !qbo?.invoices || qbo.invoices.length === 0 || !currentPayment.applications || currentPayment.applications.length === 0) {
      return 'missing';
    }
    
    const app = currentPayment.applications[0]; // Get first application
    const eservInvoiceQboId = app.invoice_qbo_id ? String(app.invoice_qbo_id) : null;
    
    if (!eservInvoiceQboId) {
      return 'missing';
    }
    
    const qboInvoice = qbo.invoices.find((inv: any) => 
      inv.success && inv.data && digitsOnly(inv.data.Id) === digitsOnly(eservInvoiceQboId)
    );
    
    if (!qboInvoice) {
      return 'missing';
    }
    
    if (qboInvoice.isActive === false) {
      return 'match_inactive';
    }
    
    return 'match_active';
  };
  
  const invoiceMatchState = getInvoiceMatchState();
  
  if (DEBUG_LOGGING) console.log('🚨 INVOICE BADGE CALCULATION STARTING:', {
    hasCurrentPayment: !!currentPayment,
    hasQbo: !!qbo,
    qboInvoicesLength: qbo?.invoices?.length || 0,
    applicationsLength: currentPayment?.applications?.length || 0,
    invoiceMatchState
  });
  
  const getInvoiceBadgeClass = (state: string) => {
    switch (state) {
      case 'match_active':
        return 'bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs';
      case 'match_inactive':
        return 'bg-amber-100 text-amber-800 px-1 py-0.5 rounded text-xs';
      case 'missing':
      case 'mismatch':
      default:
        return 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs';
    }
  };
  
  const currentPaymentQboInvoiceBadgeClass = currentPayment && 
    qbo?.invoices && 
    qbo.invoices.length > 0 && 
    currentPayment.applications && 
    currentPayment.applications.length > 0
    ? getInvoiceBadgeClass(invoiceMatchState)
    : 'bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs';
  
  if (currentPayment) {
    if (DEBUG_LOGGING) console.log('DEBUG - Badge class logic:', {
      eservCustomerQboId,
      qboCustomerIdFromData,
      actualQboCustomerId,
      customerIdsMatch,
      badgeClass: currentPaymentQboCustomerBadgeClass,
      reason: customerIdsMatch ? 'ids_match_green' : 'ids_dont_match_red'
    });
  }
  const currentPaymentApplications = useMemo(
    () => {
      const applications = buildPaymentApplications(currentPayment, qboPayload);
      if (DEBUG_LOGGING) console.log('DEBUG - buildPaymentApplications result:', {
        currentPayment: currentPayment?.payment_id,
        qboPayload: qboPayload ? {
          customers: qboPayload.customers?.length || 0,
          invoices: qboPayload.invoices?.length || 0,
          payments: qboPayload.payments?.length || 0
        } : null,
        applications: applications.map(app => ({
          invoiceId: app.app.invoice_id,
          invoiceQboId: app.invoiceQboId,
          invoiceStatusClass: app.invoiceStatusClass,
          qboInvoiceExists: app.qboInvoiceExists,
          invoiceAppliedCorrectly: app.invoiceAppliedCorrectly
        }))
      });
      return applications;
    },
    [currentPayment, qboPayload]
  );
  const currentPaymentQboErrorMessage = useMemo(() => {
    if (!currentPaymentQboEntry || currentPaymentQboEntry.success) {
      return null;
    }

    return extractQboFaultMessage(currentPaymentQboEntry.error) || 'Unknown error';
  }, [currentPaymentQboEntry]);

  const currentPaymentQboError = useMemo(() => {
    if (!currentPaymentQboEntry || currentPaymentQboEntry.success) {
      return null;
    }

    return extractQboFaultMessage(currentPaymentQboEntry.error) || currentPaymentQboEntry.error;
  }, [currentPaymentQboEntry]);

  const isCrawlOverlayActive = crawlStatus === 'crawling' && crawlUiState === 'loading';

  const currentPaymentQboIdBadgeClass = useMemo(() => {
    if (!normalizedLocalPaymentQboId) {
      return 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs';
    }

    if (!currentPaymentQboEntry) {
      return 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs';
    }

    if (!currentPaymentQboEntry.success || !currentPaymentQboData) {
      return 'bg-red-100 text-red-800 px-1 py-0.5 rounded text-xs';
    }

    return currentPaymentIdsMatch
      ? 'bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs'
      : 'bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-xs';
  }, [currentPaymentQboEntry, currentPaymentQboData, currentPaymentIdsMatch, normalizedLocalPaymentQboId]);

  useEffect(() => {
    if (!currentPayment) return;

    if (!normalizedLocalPaymentQboId && !normalizedRemotePaymentQboId) {
      return;
    }

    console.debug('Payment QBO ID comparison', {
      paymentId: currentPayment.payment_id,
      local: normalizedLocalPaymentQboId,
      remote: normalizedRemotePaymentQboId,
      match: currentPaymentIdsMatch,
      qboEntry: currentPaymentQboEntry,
    });
  }, [currentPayment, currentPaymentIdsMatch, normalizedLocalPaymentQboId, normalizedRemotePaymentQboId, currentPaymentQboEntry]);

  // Audit logging states
  const [auditLog, setAuditLog] = useState<Array<{
    timestamp: string;
    paymentId: number;
    action: 'processed' | 'needs_fix' | 'skipped' | 'started' | 'completed';
    details: {
      eservData: {
        paymentId: number;
        amount: number;
        customer: string;
        customerId: number | null;
        paymentDate: string | null;
        applications: Array<{
          invoiceId: number;
          invoiceQboId: string | null;
          canCorrect: boolean;
        }>;
      };
      qboData: {
        paymentQboId: string | null;
        customerQboId: string | null;
        synced: boolean;
      };
      notes?: string;
    };
  }>>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  // QBO API error tracking
  const [qboErrors, setQboErrors] = useState<Array<{
    timestamp: string;
    operation: string;
    error: string;
    details?: any;
  }>>([]);
  const [showQboErrors, setShowQboErrors] = useState(false);

  // QBO error logging helper function
  const logQboError = (operation: string, error: any) => {
    const resolvedMessage = extractQboFaultMessage(error?.response?.data) || extractQboFaultMessage(error) || 'Unknown QBO API error';

    const errorEntry = {
      timestamp: new Date().toISOString(),
      operation,
      error: resolvedMessage,
      details: error?.response?.data || error
    };
    
    setQboErrors(prev => [...prev, errorEntry]);
    console.warn(`QBO API Error in ${operation}:`, error?.response?.data || error);
  };

  // Audit logging helper function
  const logAuditEntry = (action: 'processed' | 'needs_fix' | 'skipped' | 'started' | 'completed', payment: AppliedPaymentCandidate, notes?: string) => {
    const auditEntry = {
      timestamp: new Date().toISOString(),
      paymentId: payment.payment_id,
      action,
      details: {
        eservData: {
          paymentId: payment.payment_id,
          amount: payment.payment_amount,
          customer: payment.company_name || `${payment.first_name} ${payment.last_name}`,
          customerId: payment.fk_bcustomer_id,
          paymentDate: payment.received_on,
          applications: (payment.applications || []).map(app => ({
            invoiceId: app.invoice_id,
            invoiceQboId: app.invoice_qbo_id,
            canCorrect: app.can_correct
          }))
        },
        qboData: {
          paymentQboId: payment.payment_qbo_id,
          customerQboId: payment.customer_qbo_id,
          synced: !!payment.payment_qbo_id
        },
        notes
      }
    };
    
    // Increment the ref counter for processed/needs_fix actions
    if (action === 'processed' || action === 'needs_fix') {
      processedCountRef.current += 1;
      if (DEBUG_LOGGING) console.log('🔢 Processed count incremented to:', processedCountRef.current);
    }
    
    setAuditLog(prev => [...prev, auditEntry]);
  };

  // Download audit log as CSV
  const downloadAuditLog = () => {
    if (auditLog.length === 0) {
      alert('No audit log entries to download');
      return;
    }

    // Calculate summary statistics
    const summary = {
      processed: auditLog.filter(entry => entry.action === 'processed').length,
      needsFix: auditLog.filter(entry => entry.action === 'needs_fix').length,
      skipped: auditLog.filter(entry => entry.action === 'skipped').length,
      started: auditLog.filter(entry => entry.action === 'started').length,
      completed: auditLog.filter(entry => entry.action === 'completed').length
    };

    // CSV headers
    const headers = ['Timestamp', 'Payment ID', 'Action', 'Details'];
    
    // Convert audit log entries to CSV rows
    const csvRows = auditLog.map(entry => [
      entry.timestamp,
      entry.paymentId || '',
      entry.action || '',
      JSON.stringify(entry.details || {})
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Add metadata as comments at the top
    const metadata = [
      `# Payment Crawl Audit Log - ${new Date().toISOString().split('T')[0]}`,
      `# Crawl ID: crawl_${Date.now()}`,
      `# Start Time: ${auditLog[0]?.timestamp || new Date().toISOString()}`,
      `# End Time: ${new Date().toISOString()}`,
      `# Total Entries: ${auditLog.length}`,
      `# Summary: Processed=${summary.processed}, Needs Fix=${summary.needsFix}, Skipped=${summary.skipped}, Started=${summary.started}, Completed=${summary.completed}`,
      ''
    ].join('\n');

    const fullCsvContent = metadata + csvContent;
    const blob = new Blob([fullCsvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-crawl-audit-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Sync functions
  const syncCustomer = async (customerId: number) => {
    if (!customerId) return;
    
    const syncKey = `customer-${customerId}`;
    setSyncingItems(prev => new Set(prev).add(syncKey));
    
    try {
      const result = await syncCustomerForReconciliation(customerId);
      if (result.success) {
        const successText = result.message || (result.qboId ? `Customer synced successfully. QBO ID: ${result.qboId}` : 'Customer synced successfully.');
        setBanner({ type: 'success', text: successText });
        await refreshCurrentPaymentWithQbo(currentPayment?.payment_id ?? null);
      } else {
        const errorText = result.error || result.message || 'Failed to sync customer';
        setBanner({ type: 'error', text: errorText });
      }
    } catch (error: any) {
      logQboError('syncCustomer', error);
      const faultErrors = error?.response?.data?.Fault?.Error;
      const faultMessage = Array.isArray(faultErrors)
        ? faultErrors.map((err: any) => [err.Message, err.Detail].filter(Boolean).join(' - ')).filter(Boolean).join('; ')
        : null;
      const errorMessage = faultMessage || error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unknown error';
      setBanner({ type: 'error', text: `Failed to sync customer: ${errorMessage}` });
    } finally {
      setSyncingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncKey);
        return newSet;
      });
    }
  };

  const syncInvoiceForCrawl = async (invoiceId: number) => {
    if (!invoiceId) return;
    
    if (DEBUG_LOGGING) console.log('🔄 syncInvoiceForCrawl called for invoice:', invoiceId);
    
    const syncKey = `invoice-${invoiceId}`;
    setSyncingItems(prev => new Set(prev).add(syncKey));
    
    try {
      const result = await syncInvoiceForReconciliation(invoiceId);
      if (DEBUG_LOGGING) console.log('✅ Invoice sync result:', result);
      if (result.success) {
        const qboId = result.qboId ? String(result.qboId) : null;
        const paymentIdForUpdate = currentPayment?.payment_id ?? null;

        if (qboId) {
          setRecentlySyncedInvoices(prev => {
            const next = new Set(prev);
            next.add(invoiceId);
            return next;
          });

          setCurrentPayment(prev => {
            if (!prev) return prev;
            if (!prev.applications?.length) return prev;
            const updatedApplications = prev.applications.map(app =>
              app.invoice_id === invoiceId
                ? { ...app, invoice_qbo_id: qboId }
                : app
            );
            return { ...prev, applications: updatedApplications };
          });

          setItems(prevItems =>
            prevItems.map(item => {
              if (!paymentIdForUpdate || item.payment_id !== paymentIdForUpdate) {
                return item;
              }
              if (!item.applications?.length) return item;
              return {
                ...item,
                applications: item.applications.map(app =>
                  app.invoice_id === invoiceId
                    ? { ...app, invoice_qbo_id: qboId }
                    : app
                ),
              };
            })
          );
        }

        setBanner({ type: 'success', text: `Invoice synced successfully. QBO ID: ${result.qboId}` });
        if (DEBUG_LOGGING) console.log('🔄 Calling refreshCurrentPaymentWithQbo...');
        await refreshCurrentPaymentWithQbo(currentPayment?.payment_id ?? null);
        if (DEBUG_LOGGING) console.log('✅ refreshCurrentPaymentWithQbo completed');
      } else {
        setBanner({ type: 'error', text: `Failed to sync invoice: ${result.message}` });
      }
    } catch (error: any) {
      logQboError('syncInvoice', error);
      setBanner({ type: 'error', text: `Failed to sync invoice: ${error?.message || 'Unknown error'}` });
    } finally {
      setSyncingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncKey);
        return newSet;
      });
    }
  };

  const syncPayment = async (paymentId: number) => {
    if (!paymentId) return;
    
    const syncKey = `payment-${paymentId}`;
    setSyncingItems(prev => new Set(prev).add(syncKey));
    
    try {
      const result = await syncPaymentForReconciliation(paymentId);
      if (result.success) {
        if (DEBUG_LOGGING) console.log('Payment sync successful, refreshing UI...', { paymentId, qboId: result.qboId });
        setBanner({ type: 'success', text: `Payment synced successfully. QBO ID: ${result.qboId}` });
        
        // Refresh the entire list to show updated payment data
        await loadPage(page, pageSize, searchTerm);
        if (DEBUG_LOGGING) console.log('loadPage completed, items updated');
        
        // Also refresh current payment if it's the one being synced
        if (currentPayment && currentPayment.payment_id === paymentId) {
          if (DEBUG_LOGGING) console.log('Refreshing current payment QBO data...');
          await refreshCurrentPaymentWithQbo(paymentId);
          if (DEBUG_LOGGING) console.log('refreshCurrentPaymentWithQbo completed');
        }
      } else {
        setBanner({ type: 'error', text: `Failed to sync payment: ${result.message}` });
      }
    } catch (error: any) {
      logQboError('syncPayment', error);
      setBanner({ type: 'error', text: `Failed to sync payment: ${error?.message || 'Unknown error'}` });
    } finally {
      setSyncingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncKey);
        return newSet;
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fixAllCurrentPayment = async () => {
    if (!currentPayment) return;
    
    setBanner({ type: 'info', text: 'Starting bulk fix for current payment...' });
    
    try {
      const promises = [];
      
      // Sync customer if needed
      if (!currentPayment.customer_qbo_id && currentPayment.fk_bcustomer_id) {
        promises.push(syncCustomer(currentPayment.fk_bcustomer_id));
      }
      
      // Sync payment if needed
      if (!currentPayment.payment_qbo_id) {
        promises.push(syncPayment(currentPayment.payment_id));
      }
      
      // Sync invoices if needed
      const unsyncedInvoices = (currentPayment.applications || [])
        .filter(app => !app.invoice_qbo_id)
        .map(app => app.invoice_id);
      
      if (unsyncedInvoices.length > 0) {
        promises.push(syncInvoicesForReconciliation(unsyncedInvoices));
      }
      
      await Promise.all(promises);
      
      setBanner({ type: 'success', text: 'Bulk fix completed! Refreshing payment data...' });
      
      await refreshCurrentPaymentCandidate(currentPayment.payment_id);
      
    } catch (error: any) {
      logQboError('fixAllCurrentPayment', error);
      setBanner({ type: 'error', text: `Bulk fix failed: ${error?.message || 'Unknown error'}` });
    }
  };

  const fixAllCurrentPaymentComplete = async () => {
    if (!currentPayment) return;

    setSyncingItems(prev => new Set(prev).add('fix-all'));
    setBanner({ type: 'info', text: 'Starting complete fix workflow: Customer → Invoice → Payment → Apply...' });

    try {
      const paymentId = currentPayment.payment_id;
      let latestPayment = currentPayment;
      const results = {
        customer: null as any,
        invoices: [] as any[],
        payment: null as any,
        applications: [] as any[]
      };
      const getQboContext = async (payment: AppliedPaymentCandidate | null) =>
        await fetchPaymentQboContext(payment, fetchQboDataForItems, qboPayload);

      let qboContext = await getQboContext(latestPayment);
      let analysis = evaluatePaymentForFix(latestPayment, qboContext);

      // Step 1: Sync customer if needed
      if (analysis.customerNeedsSync && latestPayment.fk_bcustomer_id) {
        setBanner({ type: 'info', text: 'Step 1/4: Syncing customer...' });
        results.customer = await syncCustomerForReconciliation(latestPayment.fk_bcustomer_id);
        if (!results.customer.success) {
          throw new Error(`Customer sync failed: ${results.customer.message}`);
        }
        latestPayment = (await refreshCurrentPaymentCandidate(paymentId)) || latestPayment;
        qboContext = await getQboContext(latestPayment);
        analysis = evaluatePaymentForFix(latestPayment, qboContext);
      }

      // Step 2: Sync invoices if needed
      if (analysis.invoiceIdsToSync.length > 0) {
        let processedInvoices = 0;
        let totalInvoices = analysis.invoiceIdsToSync.length;
        while (analysis.invoiceIdsToSync.length > 0) {
          const invoiceId = analysis.invoiceIdsToSync[0];
          processedInvoices += 1;
          if (analysis.invoiceIdsToSync.length > totalInvoices) {
            totalInvoices = analysis.invoiceIdsToSync.length;
          }
          setBanner({ type: 'info', text: `Step 2/4: Syncing invoice ${processedInvoices} of ${totalInvoices} (ID ${invoiceId})...` });
          const result = await syncInvoiceForReconciliation(invoiceId);
          if (!result.success) {
            throw new Error(`Invoice ${invoiceId} sync failed: ${result.message}`);
          }
          results.invoices.push(result);
          latestPayment = (await refreshCurrentPaymentCandidate(paymentId)) || latestPayment;
          qboContext = await getQboContext(latestPayment);
          analysis = evaluatePaymentForFix(latestPayment, qboContext);
        }
      }

      // Step 3: Sync payment if needed
      if (analysis.paymentNeedsSync) {
        setBanner({ type: 'info', text: 'Step 3/4: Syncing payment...' });
        results.payment = await syncPaymentForReconciliation(latestPayment.payment_id);
        if (!results.payment.success) {
          throw new Error(`Payment sync failed: ${results.payment.message}`);
        }
        latestPayment = (await refreshCurrentPaymentCandidate(paymentId)) || latestPayment;
        qboContext = await getQboContext(latestPayment);
        analysis = evaluatePaymentForFix(latestPayment, qboContext);
      }

      // Step 4: Apply payments to invoices (sparse update)
      if (latestPayment.payment_qbo_id) {
        const applyTargets = analysis.applyTargets;
        if (applyTargets.length > 0) {
          setBanner({ type: 'info', text: `Step 4/4: Applying payments to ${applyTargets.length} invoice(s) with sparse update...` });
          for (const summary of applyTargets) {
            try {
              const applyResult = await applyPaymentToInvoice({
                paymentId: latestPayment.payment_id,
                invoiceId: summary.app.invoice_id,
                qbolInvoiceId: summary.invoiceQboId!,
                qbolPaymentId: latestPayment.payment_qbo_id!,
                amount: summary.expectedAppliedCents
              });
              results.applications.push(applyResult);
            } catch (error: any) {
              console.warn(`Failed to apply payment to invoice ${summary.app.invoice_id}:`, error);
            }
          }
          latestPayment = (await refreshCurrentPaymentCandidate(paymentId)) || latestPayment;
        }
      }

      const stepSummary = [
        results.customer ? 'Customer' : null,
        results.invoices.length ? `${results.invoices.length} Invoice(s)` : null,
        results.payment ? 'Payment' : null,
        results.applications.length ? `Applied to ${results.applications.length} Invoice(s)` : 'No invoice applications needed'
      ].filter(Boolean).join(' • ');

      setBanner({ 
        type: 'success', 
        text: `Complete fix workflow finished! ${stepSummary || 'No changes were required.'}`
      });
      
      // Refresh current payment data
      await refreshCurrentPaymentCandidate(paymentId);
      
    } catch (error: any) {
      logQboError('fixAllCurrentPaymentComplete', error);
      setBanner({ type: 'error', text: `Complete fix workflow failed: ${error?.message || 'Unknown error'}` });
    } finally {
      setSyncingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete('fix-all');
        return newSet;
      });
    }
  };

  const applyPaymentToInvoices = async () => {
    if (!currentPayment || !currentPayment.payment_qbo_id) {
      setBanner({ type: 'error', text: 'Payment must be synced to QBO before applying to invoices' });
      return;
    }

    const fetchedQbo = await fetchQboDataForItems([currentPayment]);
    const qboContext = fetchedQbo ?? qboPayload;
    if (!qboContext) {
      setBanner({ type: 'error', text: 'Unable to retrieve QBO details. Please fetch QBO data and try again.' });
      return;
    }
    const applicationSummaries = buildPaymentApplications(currentPayment, qboContext);
    const applyTargets = applicationSummaries.filter(summary => summary.shouldShowApply && !summary.applyDisabledReason);

    if (applyTargets.length === 0) {
      setBanner({ type: 'info', text: 'All invoices already have the correct payment amount applied in QuickBooks.' });
      return;
    }

    setBanner({ type: 'info', text: `Applying payment to ${applyTargets.length} invoice(s)...` });

    try {
      const applyPromises = applyTargets.map(summary =>
        applyPaymentToInvoice({
          paymentId: currentPayment.payment_id,
          invoiceId: summary.app.invoice_id,
          qbolInvoiceId: summary.invoiceQboId!,
          qbolPaymentId: currentPayment.payment_qbo_id!,
          amount: summary.expectedAppliedCents
        })
      );

      await Promise.all(applyPromises);
      setBanner({ type: 'success', text: `Payment successfully applied to ${applyTargets.length} invoice(s)!` });
      
      // Refresh current payment data
      await refreshCurrentPaymentWithQbo(currentPayment.payment_id);
    } catch (error: any) {
      setBanner({ type: 'error', text: `Failed to apply payment: ${error?.message || 'Unknown error'}` });
    }
  };

  const loadPage = async (p = page, ps = pageSize, search = searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAppliedPaymentCandidates(p, ps, search);
      if (DEBUG_LOGGING) console.log('loadPage response:', { 
        total: resp.total, 
        itemsCount: resp.items?.length,
        paymentIds: resp.items?.map(item => ({ id: item.payment_id, qboId: item.payment_qbo_id }))
      });
      setItems(resp.items || []);
      setTotal(resp.total || 0);
      setPage(resp.page || p);
      setPageSize(resp.pageSize || ps);
      
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  // Independent QBO data fetching functions
  const fetchQboPayments = async (paymentQboIds: string[]) => {
    if (paymentQboIds.length === 0) return [];
    
    try {
      const qboData = await fetchAppliedPaymentsQboDetails(paymentQboIds, [], []);
      return qboData?.payments || [];
    } catch (error) {
      console.error('Failed to fetch QBO payments:', error);
      return [];
    }
  };

  const fetchQboInvoices = async (invoiceQboIds: string[]) => {
    if (invoiceQboIds.length === 0) return [];
    
    try {
      const qboData = await fetchAppliedPaymentsQboDetails([], invoiceQboIds, []);
      return qboData?.invoices || [];
    } catch (error) {
      console.error('Failed to fetch QBO invoices:', error);
      return [];
    }
  };

  const fetchQboCustomers = async (customerQboIds: string[]) => {
    if (customerQboIds.length === 0) {
      if (DEBUG_LOGGING) console.log('🔍 fetchQboCustomers: No customer IDs provided, skipping');
      return [];
    }
    
    if (DEBUG_LOGGING) console.log('🔍 fetchQboCustomers: Fetching customers with IDs:', customerQboIds);
    
    try {
      const qboData = await fetchAppliedPaymentsQboDetails([], [], customerQboIds);
      if (DEBUG_LOGGING) console.log('🔍 fetchQboCustomers: API response:', {
        success: !!qboData,
        customers: qboData?.customers?.map(c => ({ id: c.id, success: c.success, hasData: !!c.data })) || []
      });
      return qboData?.customers || [];
    } catch (error) {
      console.error('Failed to fetch QBO customers:', error);
      if (DEBUG_LOGGING) console.log('🔍 fetchQboCustomers: Error details:', error);
      return [];
    }
  };

  const fetchQboDataForItems = async (items: AppliedPaymentCandidate[]) => {
    setQboLoading(true);
    let fetched: AppliedPaymentsQboDetails | null = null;
    
    if (DEBUG_LOGGING) console.log('🔍 fetchQboDataForItems called with:', {
      itemsCount: items.length,
      currentPayment: currentPayment ? {
        payment_id: currentPayment.payment_id,
        customer_qbo_id: currentPayment.customer_qbo_id,
        customer_qbo_id_type: typeof currentPayment.customer_qbo_id
      } : null
    });
    
    try {
      // Extract all QBO IDs independently
      const paymentQboIds = items
        .map(item => item.payment_qbo_id)
        .filter(Boolean) as string[];
      
      const invoiceQboIds = Array.from(new Set(
        items.flatMap(item => 
          (item.applications || [])
            .map(app => app.invoice_qbo_id)
            .filter(Boolean)
        )
      )) as string[];

      const customerQboIds = Array.from(new Set([
        ...items.map(item => {
            const rawId = item.customer_qbo_id;
            if (!rawId) return null;
            // Extract just the numeric ID from "QBO: 73" format
            const extractedId = String(rawId).replace(/.*\/\s*QBO:\s*/, '').replace(/^QBO:\s*/, '');
          if (DEBUG_LOGGING) console.log('🔍 Customer ID extraction from items:', {
            rawId,
            extractedId,
            originalString: String(rawId)
          });
            return extractedId || rawId;
        }).filter(Boolean),
        // Always include current payment's customer ID if it exists
        ...(currentPayment?.customer_qbo_id ? (() => {
          const currentRawId = currentPayment.customer_qbo_id;
          const currentExtractedId = String(currentRawId).replace(/.*\/\s*QBO:\s*/, '').replace(/^QBO:\s*/, '');
          if (DEBUG_LOGGING) console.log('🔍 Customer ID extraction from current payment:', {
            rawId: currentRawId,
            extractedId: currentExtractedId,
            originalString: String(currentRawId)
          });
          return currentExtractedId || String(currentRawId);
        })() : [])
      ])) as string[];

      if (DEBUG_LOGGING) console.log('🔍 INDEPENDENT QBO FETCH - Starting parallel lookups:', {
            paymentQboIds,
            invoiceQboIds, 
            customerQboIds,
        paymentCount: paymentQboIds.length,
        invoiceCount: invoiceQboIds.length,
        customerCount: customerQboIds.length,
        customerQboIdsRaw: customerQboIds,
        customerQboIdsStringified: JSON.stringify(customerQboIds)
      });

      // Fetch all QBO data in parallel - each independent of the others
      const [payments, invoices, customers] = await Promise.all([
        fetchQboPayments(paymentQboIds),
        fetchQboInvoices(invoiceQboIds),
        fetchQboCustomers(customerQboIds)
      ]);

      if (DEBUG_LOGGING) console.log('✅ INDEPENDENT QBO FETCH - Results:', {
        payments: payments.map(p => ({ id: p.id, success: p.success, hasData: !!p.data })),
        invoices: invoices.map(i => ({ id: i.id, success: i.success, hasData: !!i.data })),
        customers: customers.map(c => ({ id: c.id, success: c.success, hasData: !!c.data }))
      });

      // Combine results into the expected format
      fetched = {
        payments,
        invoices,
        customers,
        realmId: 'unknown' // We'll get this from the first successful call if needed
      };

      setQbo(fetched);
    } catch (e) {
      console.error('Failed to fetch QBO data:', e);
      setError('Failed to fetch QuickBooks data');
      fetched = null;
    } finally {
      setQboLoading(false);
    }
    return fetched;
  };

  const refreshCurrentPaymentWithQbo = async (targetPaymentId?: number | null) => {
    try {
      if (DEBUG_LOGGING) console.log('🔄 refreshCurrentPaymentWithQbo called for payment:', targetPaymentId);
      const updated = await refreshCurrentPaymentCandidate(targetPaymentId);
      if (!updated) {
        if (DEBUG_LOGGING) console.log('❌ No updated payment found');
        return null;
      }

      if (DEBUG_LOGGING) console.log('✅ Updated payment data:', {
        payment_id: updated.payment_id,
        customer_qbo_id: updated.customer_qbo_id,
        invoice_qbo_ids: updated.applications?.map(app => app.invoice_qbo_id)
      });

      let qboDetails: AppliedPaymentsQboDetails | null = null;
      try {
        qboDetails = await fetchQboDataForItems([updated]);
        if (DEBUG_LOGGING) console.log('✅ QBO details fetched:', {
          hasQboDetails: !!qboDetails,
          customers: qboDetails?.customers?.length || 0,
          invoices: qboDetails?.invoices?.length || 0,
          payments: qboDetails?.payments?.length || 0
        });
        
        // Update the qbo state with the new details so the UI reflects the changes
        if (qboDetails) {
          setQbo(qboDetails);
          if (DEBUG_LOGGING) console.log('✅ QBO state updated');

          if (recentlySyncedInvoices.size && updated.applications?.length) {
            const confirmedInvoiceIds = new Set<number>();
            const remoteInvoices = new Map<string, any>();
            (qboDetails.invoices || []).forEach((entry: any) => {
              if (entry?.success && entry?.data?.Id != null) {
                remoteInvoices.set(String(entry.data.Id), entry);
              }
            });

            updated.applications.forEach(app => {
              if (!app.invoice_id || !app.invoice_qbo_id) return;
              if (remoteInvoices.has(String(app.invoice_qbo_id))) {
                confirmedInvoiceIds.add(app.invoice_id);
              }
            });

            if (confirmedInvoiceIds.size > 0) {
              setRecentlySyncedInvoices(prev => {
                const next = new Set(prev);
                confirmedInvoiceIds.forEach(id => next.delete(id));
                return next;
              });
            }
          }
        }
      } catch (refreshError) {
        console.error('❌ Failed to refresh QBO details after update:', refreshError);
      }

      if (crawlStatus === 'crawling') {
        setCrawlUiState('ready');
        setCrawlAdvanceCountdown(null);
        if (qboDetails) {
          evaluateCrawlPayment(updated, qboDetails);
        }
      }

      return { payment: updated, qboDetails };
    } catch (error) {
      console.error('❌ Failed to refresh current payment state:', error);
      return null;
    }
  };

  const begin = () => loadPage(1, pageSize, searchTerm);

  const fetchQboDataForSelected = async () => {
    if (selectedIds.size === 0) return;
    
    setQboLoading(true);
    setError(null);
    
    try {
      const selectedItems = getFilteredItems(items).filter(item => selectedIds.has(item.payment_id));
      if (selectedItems.length > 0) {
        await fetchQboDataForItems(selectedItems);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load QBO data');
    } finally {
      setQboLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    setSummaryLoading(true);
    try {
      // Fetch local summary data
      const localSummary = await getAppliedPaymentsSummary();
      
      // Fetch QBO summary data if connection is available
      let qboSummary = null;
      if (connectionStatus) {
        try {
          // We'll need to create a new API endpoint for QBO summary data
          // For now, let's use the existing QBO data if available
          if (qbo) {
            const qboData = qbo;
            qboSummary = {
              totalInvoices: qboData?.invoices?.length || 0,
              paidInFull: qboData?.invoices?.filter((inv: any) => inv.success && inv.data && inv.data.Balance === 0).length || 0,
              totalPayments: qboData?.payments?.length || 0,
              totalAmount: qboData?.payments?.reduce((sum: number, p: any) => sum + (p.success ? (Number(p.data?.TotalAmt) || 0) : 0), 0) || 0
            };
          }
        } catch (e) {
          console.warn('Failed to fetch QBO summary data:', e);
        }
      }
      
      setSummaryData({
        local: {
          invoicesToScan: localSummary.invoicesToScan || 0,
          mismatches: localSummary.mismatches || 0,
          lastRun: localSummary.lastRun
        },
        qbo: qboSummary
      });
    } catch (e: any) {
      console.error('Failed to fetch summary data:', e);
    } finally {
      setSummaryLoading(false);
    }
  };

  const refreshCurrentPaymentCandidate = async (paymentId?: number | null) => {
    try {
      const targetId = paymentId ?? currentPayment?.payment_id ?? null;
      const search = targetId != null
        ? String(targetId)
        : (startPaymentId || '');

      if (DEBUG_LOGGING) console.log('🔄 refreshCurrentPaymentCandidate called:', { paymentId, targetId, search });

      const pageToRequest = search ? 1 : currentPage;
      const response = await getAppliedPaymentCandidates(pageToRequest, 1, search);
      const candidates = response.items || [];
      
      if (DEBUG_LOGGING) console.log('📋 Found candidates:', candidates.length);
      
      if (candidates.length === 0) {
        if (DEBUG_LOGGING) console.log('❌ No candidates found');
        return null;
      }

      const match = targetId != null
        ? candidates.find(item => item.payment_id === targetId)
        : candidates[0];

      const nextPayment = match || candidates[0];
      
      if (DEBUG_LOGGING) console.log('✅ Next payment data:', {
        payment_id: nextPayment.payment_id,
        customer_qbo_id: nextPayment.customer_qbo_id,
        invoice_qbo_ids: nextPayment.applications?.map(app => app.invoice_qbo_id),
        applications_count: nextPayment.applications?.length || 0
      });
      
      setCurrentPayment(nextPayment);
      return nextPayment;
    } catch (error) {
      console.error('❌ Failed to refresh current payment candidate:', error);
      return null;
    }
  };

  const checkConnectionStatus = async () => {
    try {
      // Check connection status
      const result = await pingQboCompanyLegacy();
      setConnectionStatus(result);
      
      return true; // Legacy function returns the data directly if successful
    } catch (error) {
      logQboError('checkConnection', error);
      setConnectionStatus(null);
      return false;
    }
  };

  // Auto-load data when component mounts
  useEffect(() => {
    begin();
    checkConnectionStatus();
    checkQboConnection(); // Also check QBO connection for Dev tab
    fetchQboTokenInfo(); // Fetch token info for Dev tab
    fetchSummaryData();
  }, []); // Empty dependency array - only run once on mount

  // Check QBO connection and fetch totals when crawl or dev tab becomes active
  useEffect(() => {
    if ((activeTab === 'crawl' || activeTab === 'dev') && qboConnectionStatus === 'checking') {
      checkQboConnection();
    }
    // Auto-fetch QBO data when crawl tab is activated
    if (activeTab === 'crawl' && crawlStatus === 'idle') {
      fetchCrawlTotals();
    }
  }, [activeTab, qboConnectionStatus, crawlStatus]);

  // Helper function to check QBO connection
  const checkQboConnection = async () => {
    try {
      const response = await pingQboCompanyLegacy();
      if (response) {
        setQboConnectionStatus('connected');
      } else {
        setQboConnectionStatus('disconnected');
      }
    } catch (error) {
      console.error('QBO connection check failed:', error);
      setQboConnectionStatus('error');
    }
  };

  // Helper function to fetch QBO token info
  const fetchQboTokenInfo = async () => {
    try {
      const tokenInfo = await getQboTokenInfo();
      setQboTokenInfo(tokenInfo);
    } catch (error) {
      console.error('Failed to fetch QBO token info:', error);
      setQboTokenInfo(null);
    }
  };

  // Fetch summary data when connection status changes
  useEffect(() => {
    if (connectionStatus) {
      fetchSummaryData();
    }
  }, [connectionStatus]);

  const evaluateCrawlPayment = (
    payment: AppliedPaymentCandidate | null,
    qboDetails: AppliedPaymentsQboDetails | null
  ): boolean => {
    if (DEBUG_LOGGING) console.log('🔍🔍🔍 ========== evaluateCrawlPayment CALLED ==========');
    if (DEBUG_LOGGING) console.log('🔍 Payment ID:', payment?.payment_id);
    if (DEBUG_LOGGING) console.log('🔍 Has QBO Details:', !!qboDetails);
    if (DEBUG_LOGGING) console.log('🔍 Current Progress:', crawlProgress);
    if (DEBUG_LOGGING) console.log('🔍 Processed Count (ref):', processedCountRef.current);
    
    // IMMEDIATE CHECK: If processed count >= original payment count, STOP EVERYTHING
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP in evaluateCrawlPayment: Processed count >= total - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return false;
    }
    
    // FORCE STOP if we've reached or exceeded the total - check before doing anything else
    if (crawlProgress && crawlProgress.current >= crawlProgress.total) {
      if (DEBUG_LOGGING) console.log('🚨 FORCE STOP in evaluateCrawlPayment: Current at or exceeds total - current:', crawlProgress.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${crawlProgress.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return false;
    }
    
    if (!payment || !qboDetails) {
      if (DEBUG_LOGGING) console.log('❌ No payment or QBO details');
      clearCrawlAutoAdvance();
      setCrawlUiState('ready');
      return false;
    }

    // Check if payment has QBO ID and QBO data exists
    const qboPaymentEntry = Array.isArray(qboDetails?.payments)
      ? qboDetails.payments.find((p: any) => String(p.id) === String(payment.payment_qbo_id))
      : null;
    
    const hasQboPayment = Boolean(qboPaymentEntry?.success && qboPaymentEntry?.data);
    
    if (!payment.payment_qbo_id || !hasQboPayment) {
      if (DEBUG_LOGGING) console.log('❌ No QBO payment ID or QBO payment data');
      setCrawlUiState('ready');
      
      // If stop on mismatch is enabled, pause the crawl
      if (stopOnMismatch && crawlStatus === 'crawling') {
        if (DEBUG_LOGGING) console.log('🛑 Stopping crawl - payment missing QBO data (stopOnMismatch: ON)');
        setCrawlStatus('paused');
        setBanner({ type: 'error', text: 'Crawl stopped - Payment has no QBO data. Review and mark as processed or needs fix.' });
      }
      
      return false;
    }

    // Now check if the data actually matches
    const eservCustomerQboId = payment.customer_qbo_id ? String(payment.customer_qbo_id).replace('QBO:', '') : null;
    const qboPaymentCustomerId = qboPaymentEntry?.data?.CustomerRef?.value;
    
    // Check customer ID match
    const customerIdsMatch = eservCustomerQboId && qboPaymentCustomerId && 
      String(eservCustomerQboId) === String(qboPaymentCustomerId);
    
    // Check amount match - EServ stores in cents, QBO in dollars
    const eservAmountRaw = parseFloat(String(payment.payment_amount || '0'));
    const eservAmount = eservAmountRaw / 100; // Convert cents to dollars
    const qboAmount = parseFloat(String(qboPaymentEntry?.data?.TotalAmt || '0'));
    const amountsMatch = Math.abs(eservAmount - qboAmount) < 0.01;
    
    // Check if applied amounts on each invoice match
    const applicationSummaries = buildPaymentApplications(payment, qboDetails);
    const allInvoicesAppliedCorrectly = applicationSummaries.length > 0 && 
      applicationSummaries.every(app => app.invoiceAppliedCorrectly);
    const hasAnyMismatchedApplications = applicationSummaries.some(app => !app.invoiceAppliedCorrectly);
    
    // Enhanced logging with visual indicators
    if (DEBUG_LOGGING) console.log('🔍 Payment Match Analysis:');
    if (DEBUG_LOGGING) console.log(`👤 Customer ID Match: ${customerIdsMatch ? '✅' : '❌'} | EServ: ${eservCustomerQboId} | QBO: ${qboPaymentCustomerId}`);
    if (DEBUG_LOGGING) console.log(`💰 Amount Match: ${amountsMatch ? '✅' : '❌'} | EServ: $${eservAmount.toFixed(2)} (${eservAmountRaw}¢) | QBO: $${qboAmount.toFixed(2)} | Diff: $${Math.abs(eservAmount - qboAmount).toFixed(2)}`);
    if (DEBUG_LOGGING) console.log(`📋 Invoice Applications: ${allInvoicesAppliedCorrectly ? '✅' : '❌'} | Total: ${applicationSummaries.length} | Mismatched: ${applicationSummaries.filter(app => !app.invoiceAppliedCorrectly).length}`);
    applicationSummaries.forEach((app, idx) => {
      if (DEBUG_LOGGING) console.log(`   Invoice ${idx + 1}: ${app.invoiceAppliedCorrectly ? '✅' : '❌'} | Invoice ID: ${app.app.invoice_id} | Expected: ${app.expectedAppliedCents}¢ | QBO: ${app.qboAppliedCents}¢`);
    });
    if (DEBUG_LOGGING) console.log('📊 Full Details:', {
      eservAmountRaw,
      eservAmount,
      qboAmount,
      amountsMatch,
      difference: Math.abs(eservAmount - qboAmount),
      allInvoicesAppliedCorrectly,
      hasAnyMismatchedApplications,
      applicationCount: applicationSummaries.length
    });
    
    
    // If customer, amount, AND all invoice applications match, start countdown
    if (customerIdsMatch && amountsMatch && allInvoicesAppliedCorrectly) {
      if (DEBUG_LOGGING) console.log('🎉 ✅ FULL MATCH - Payment fully matches QBO data (including all invoice applications)');
      if (DEBUG_LOGGING) console.log('🔍 Current progress when match found - current:', crawlProgress?.current, 'total:', crawlProgress?.total);
      
      // Check if we've already processed all payments before starting countdown
      if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
        if (DEBUG_LOGGING) console.log('🚀 Match found but already processed all payments - processed:', processedCountRef.current, 'total:', crawlProgress.total);
        setCrawlStatus('completed');
        setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
        setCrawlUiState('idle');
        setCurrentPayment(null);
        return true;
      }
      
      if (DEBUG_LOGGING) console.log('Starting auto-advance countdown');
      setCrawlUiState('matched');
      
      // Log audit entry for auto-advance match
      logAuditEntry('processed', payment, '✅ MATCHED - Payment fully matches QBO data (including all invoice applications) - auto-advancing in 3 seconds');
      
      startCountdown();
      return true;
    } else {
      if (DEBUG_LOGGING) console.log('🚫 ❌ MISMATCH - Payment does not fully match QBO data');
      if (DEBUG_LOGGING) console.log(`   Customer Match: ${customerIdsMatch ? '✅' : '❌'} | Amount Match: ${amountsMatch ? '✅' : '❌'} | Invoice Applications: ${allInvoicesAppliedCorrectly ? '✅' : '❌'}`);
      if (DEBUG_LOGGING) console.log('📋 Mismatch Details:', {
        customerIdsMatch,
        amountsMatch,
        allInvoicesAppliedCorrectly,
        hasAnyMismatchedApplications,
        eservCustomerQboId,
        qboPaymentCustomerId,
        eservAmount: eservAmount,
        qboAmount: qboAmount,
        eservAmountCents: payment.payment_amount,
        qboAmountDollars: qboPaymentEntry?.data?.TotalAmt,
        applicationSummaries: applicationSummaries.map(app => ({
          invoiceId: app.app.invoice_id,
          expected: app.expectedAppliedCents,
          qbo: app.qboAppliedCents,
          matches: app.invoiceAppliedCorrectly
        }))
      });
      
      // Log audit entry for mismatch
      let mismatchReason = 'Payment does not match QBO data:';
      if (!customerIdsMatch) mismatchReason += ' Customer ID mismatch.';
      if (!amountsMatch) mismatchReason += ' Total amount mismatch.';
      if (!allInvoicesAppliedCorrectly) mismatchReason += ` Applied amounts mismatch on ${applicationSummaries.filter(app => !app.invoiceAppliedCorrectly).length} invoice(s).`;
      logAuditEntry('needs_fix', payment, `❌ NOT MATCHED - ${mismatchReason} - requires manual review`);
      
      // If stop on mismatch is enabled, stop the crawl
      if (stopOnMismatch) {
        setCrawlStatus('paused');
        setCrawlUiState('ready');
        setBanner({ type: 'error', text: `Crawl stopped: ${mismatchReason}` });
      } else {
        // Continue advancing to next payment even with mismatch
        if (DEBUG_LOGGING) console.log('🔄 NOT MATCHED - Mismatch detected but continuing to next payment (Stop on Mismatch: OFF)');
        if (DEBUG_LOGGING) console.log('🔄 Audit log count after mismatch:', auditLog.length);
        
        // Set UI state to loading and advance immediately (like auto-advance)
        setCrawlUiState('loading');
        setTimeout(() => {
          goToNextPayment();
        }, 1000); // 1 second delay to show the mismatch briefly
      }
      
      return false;
    }
  };

  const startCountdown = () => {
    if (DEBUG_LOGGING) console.log('startCountdown called');
    
    // NUCLEAR OPTION: HARD STOP - if current >= total, STOP EVERYTHING
    if (crawlProgress && crawlProgress.current >= crawlProgress.total) {
      if (DEBUG_LOGGING) console.log('🚨🚨🚨 NUCLEAR HARD STOP in startCountdown: Current at or exceeds total - current:', crawlProgress.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'error', text: `CRAWL STOPPED! Processed ${crawlProgress.current} payments but only ${crawlProgress.total} were expected.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      clearCrawlAutoAdvance();
      setCrawlAdvanceCountdown(null);
      return;
    }
    
    clearCrawlAutoAdvance();
    
    let countdown = 3;
    setCrawlAdvanceCountdown(countdown);
    
    const interval = setInterval(() => {
      // Check if auto-advance is paused
      if (isAutoAdvancePaused) {
        if (DEBUG_LOGGING) console.log('Auto-advance paused, skipping countdown');
        return;
      }
      
      // NUCLEAR OPTION: HARD STOP - if current >= total, STOP EVERYTHING
      if (crawlProgress && crawlProgress.current >= crawlProgress.total) {
        if (DEBUG_LOGGING) console.log('🚨🚨🚨 NUCLEAR HARD STOP during countdown: Current at or exceeds total - current:', crawlProgress.current, 'total:', crawlProgress.total);
        clearInterval(interval);
        setCrawlAdvanceCountdown(null);
        setCrawlStatus('completed');
        setBanner({ type: 'error', text: `CRAWL STOPPED! Processed ${crawlProgress.current} payments but only ${crawlProgress.total} were expected.` });
        setCrawlUiState('idle');
        setCurrentPayment(null);
        clearCrawlAutoAdvance();
        return;
      }
      
      countdown--;
      if (DEBUG_LOGGING) console.log('Countdown:', countdown);
      setCrawlAdvanceCountdown(countdown);
      
      if (countdown <= 0) {
        if (DEBUG_LOGGING) console.log('Countdown finished, auto-processing payment and advancing');
        clearInterval(interval);
        setCrawlAdvanceCountdown(null);
        
        // IMMEDIATE CHECK BEFORE LOGGING: If processed count >= original payment count, STOP
        if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
          if (DEBUG_LOGGING) console.log('🚨 STOP before logging: Already at limit - processed:', processedCountRef.current, 'total:', crawlProgress.total);
          setCrawlStatus('completed');
          setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
          setCrawlUiState('idle');
          setCurrentPayment(null);
          clearCrawlAutoAdvance();
          return;
        }
        
        // Log audit entry
        if (currentPayment) {
          logAuditEntry('processed', currentPayment, '✅ MATCHED - Payment fully matches QBO data - auto-advancing');
        }
        
        // IMMEDIATE CHECK AFTER LOGGING: If processed count >= original payment count, STOP
        if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
          if (DEBUG_LOGGING) console.log('🚨 STOP after logging: Processed count >= total - processed:', processedCountRef.current, 'total:', crawlProgress.total);
          setCrawlStatus('completed');
          setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
          setCrawlUiState('idle');
          setCurrentPayment(null);
          clearCrawlAutoAdvance();
          return;
        }
        
        // Auto-advance to next payment
        setTimeout(() => {
          goToNextPayment();
        }, 0);
      }
    }, 1000);
    
    crawlAutoAdvanceIntervalRef.current = interval;
  };

  const goToNextPayment = async () => {
    if (DEBUG_LOGGING) console.log('goToNextPayment called');
    
    // IMMEDIATE CHECK: If processed count >= original payment count, STOP EVERYTHING
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP: Processed count >= original payment count - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }
    
    // Get current values from state at the time of execution
    const currentPaymentValue = currentPayment;
    const crawlProgressValue = crawlProgress;
    const crawlStatusValue = crawlStatus;
    
    if (DEBUG_LOGGING) console.log('Current payment:', currentPaymentValue?.payment_id);
    if (DEBUG_LOGGING) console.log('Crawl progress:', crawlProgressValue);
    if (DEBUG_LOGGING) console.log('Crawl status:', crawlStatusValue);
    
    // If we don't have crawlProgress, create a basic one
    if (!crawlProgressValue) {
      if (DEBUG_LOGGING) console.log('No crawlProgress, creating basic progress object');
      setCrawlProgress({
        current: 1,
        total: 100, // Will be updated when we get the response
        processed: 0,
        reconciled: 0,
        needsFix: 0
      });
    }

    clearCrawlAutoAdvance();
    setCrawlAdvanceCountdown(null);
    setCrawlUiState('loading');

    try {
      const nextPage = currentPage + 1;
      if (DEBUG_LOGGING) console.log('🚀 Loading next page:', nextPage, 'current:', currentPage, 'items length:', items.length);
      
      // Simple check: if we've reached the total number of payments, stop
      if (crawlProgressValue && crawlProgressValue.current >= crawlProgressValue.total) {
        if (DEBUG_LOGGING) console.log('🚨 Reached end of payments - completing crawl');
        setCrawlStatus('completed');
        setBanner({ type: 'info', text: `Crawl completed! Processed ${crawlProgressValue.current} of ${crawlProgressValue.total} payments.` });
        setCrawlUiState('idle');
        setCurrentPayment(null);
        return;
      }
      
      // Safety check to prevent infinite loops
      if (nextPage > 1000) {
        console.error('🚀 Safety limit reached - stopping crawl to prevent infinite loop');
        setCrawlStatus('completed');
        setBanner({ type: 'error', text: 'Crawl stopped - safety limit reached to prevent infinite loop.' });
        setCrawlUiState('idle');
        return;
      }
      
      const response = await getAppliedPaymentCandidates(nextPage, 1, '');
      if (DEBUG_LOGGING) console.log('🚀 Response received:', { itemsCount: response.items?.length, total: response.total, nextPage });

      if (response.items && response.items.length > 0) {
        const nextPayment = response.items[0];
        
        // Prevent processing the same payment twice
        if (currentPaymentValue && nextPayment.payment_id === currentPaymentValue.payment_id) {
          console.error('⚠️ Received same payment twice! Payment ID:', nextPayment.payment_id, 'Page:', nextPage);
          setCrawlStatus('completed');
          setBanner({ type: 'error', text: 'Crawler detected loop - completed crawl to prevent infinite loop.' });
          return;
        }
        
        if (DEBUG_LOGGING) console.log('Next payment loaded:', nextPayment.payment_id, 'Previous payment:', currentPaymentValue?.payment_id);
        
        // Update progress FIRST before setting payment and evaluating
        if (crawlProgressValue) {
          const newCurrent = crawlProgressValue.current + 1;
          
          // Check if we've reached the end BEFORE updating progress
          if (newCurrent > crawlProgressValue.total) {
            if (DEBUG_LOGGING) console.log('🚨 FORCE STOP: Would exceed total - current:', newCurrent, 'total:', crawlProgressValue.total);
            setCrawlStatus('completed');
            setBanner({ type: 'info', text: `Crawl completed! Processed ${crawlProgressValue.current} of ${crawlProgressValue.total} payments.` });
            setCrawlUiState('idle');
            setCurrentPayment(null);
            return;
          }
          
          setCrawlProgress(prev => prev ? { ...prev, current: newCurrent } : {
            current: newCurrent,
            total: crawlProgressValue?.total || 1,
            processed: 0,
            reconciled: 0,
            needsFix: 0
          });
        } else {
          // Create a basic progress object
          setCrawlProgress({
            current: nextPage,
            total: response.total || nextPage,
            processed: 0,
            needsFix: 0,
            reconciled: 0
          });
        }
        
        if (DEBUG_LOGGING) console.log('🚀 Setting new payment and page - payment:', nextPayment.payment_id, 'page:', nextPage);
        setCurrentPayment(nextPayment);
        setCurrentPage(nextPage);
        if (DEBUG_LOGGING) console.log('🚀 Payment and page set - UI should update now');

        // Fetch QBO data for the new payment
        if (DEBUG_LOGGING) console.log('Fetching QBO data for new payment');
        const qboData = await fetchQboDataForItems([nextPayment]);
        if (DEBUG_LOGGING) console.log('QBO data fetched:', !!qboData);
        
        if (nextPayment.payment_qbo_id && !qboData) {
          setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${nextPayment.payment_id}.` });
        }

        // Update crawl totals with fresh QBO data
        if (qboData) {
          setCrawlTotals(prev => prev ? {
            ...prev,
            qbo: {
              totalCustomers: qboData.customers?.length || 0,
              totalInvoices: qboData.invoices?.length || 0,
              totalPayments: qboData.payments?.length || 0,
              paidInvoices: qboData.invoices?.filter((inv: any) => inv.success && inv.data && inv.data.Balance === 0).length || 0,
              unpaidInvoices: qboData.invoices?.filter((inv: any) => inv.success && inv.data && inv.data.Balance > 0).length || 0,
              totalAmount: qboData.payments?.reduce((sum: number, p: any) => sum + (p.success ? (Number(p.data?.TotalAmt) || 0) : 0), 0) || 0
            }
          } : null);
        }

        // Evaluate the new payment (this will trigger countdown if it matches)
        if (DEBUG_LOGGING) console.log('🚀 Evaluating new payment - payment ID:', nextPayment.payment_id);
        evaluateCrawlPayment(nextPayment, qboData);
        if (DEBUG_LOGGING) console.log('🚀 Evaluation complete - UI should show new payment');
      } else {
        // No more payments
        if (DEBUG_LOGGING) console.log('🚀 No more payments, completing crawl');
        if (DEBUG_LOGGING) console.log('🚀 Final state - page:', nextPage, 'total from API:', response.total);
        setCrawlStatus('completed');
        setBanner({ type: 'info', text: 'Crawl completed! All payments have been processed.' });
        setCrawlUiState('idle');
        setCurrentPayment(null); // Clear current payment when done
      }
    } catch (error: any) {
      console.error('🚀 Failed to load next payment:', error);
      setBanner({ type: 'error', text: `Failed to load next payment: ${error?.message || 'Unknown error'}` });
      setCrawlUiState('ready');
    }
  };

  // Evaluation is now called directly after fetching QBO data, no need for this effect

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadPage(1, pageSize, searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);


  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      // First check connection status
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const isConnected = await checkConnectionStatus();
      
      // Refresh payment candidates
      const resp = await getAppliedPaymentCandidates(page, pageSize, searchTerm);
      setItems(resp.items || []);
      setTotal(resp.total || 0);
      
      // Refresh QBO data for current items
      if (resp.items && resp.items.length > 0) {
        await fetchQboDataForItems(resp.items);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (n?: number | null) => {
    if (n == null) return '—';
    try {
      // Convert cents to dollars by dividing by 100
      const dollars = Number(n) / 100;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
    } catch {
      return String(n);
    }
  };

  const formatDate = (s?: string | null) => {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString();
  };

  // Filter logic (only works after QBO data is loaded)
  const getFilteredItems = (items: AppliedPaymentCandidate[]) => {
    // If QBO data isn't loaded yet, don't apply filters
    if (!qbo) {
      return items;
    }

    return items.filter(item => {
      const qboDataPayload = qbo;
      const qboPayment = qboDataPayload?.payments?.find((p: any) => String(p.id) === String(item.payment_qbo_id));
      const qboPaymentData = qboPayment && qboPayment.success ? qboPayment.data : null;

      // Missing in QBO filter
      if (showMissingInQbo) {
        const isPaymentMissing = !item.payment_qbo_id || !qboPaymentData;
        const isCustomerMissing = item.customer_qbo_id && !qboDataPayload?.customers?.find((c: any) => String(c.id) === String(item.customer_qbo_id) && c.success);
        const hasMissingInvoices = item.applications?.some(app => 
          app.invoice_qbo_id && !qboDataPayload?.invoices?.find((inv: any) => String(inv.id) === String(app.invoice_qbo_id) && inv.success)
        );
        
        if (!isPaymentMissing && !isCustomerMissing && !hasMissingInvoices) return false;
      }

      // Mismatch filter
      if (showMismatch) {
        let hasMismatch = false;

        // Check payment amount mismatch
        if (qboPaymentData) {
          const eservAmountCents = Number(item.payment_amount || 0);
          const qboAmountCents = Math.round((Number(qboPaymentData.TotalAmt) || 0) * 100);
          if (eservAmountCents !== qboAmountCents) {
            hasMismatch = true;
          }
        }

        // Check applied amount mismatches
        if (!hasMismatch && qboPaymentData) {
          hasMismatch = item.applications?.some(app => {
            if (!app.invoice_qbo_id) return false;
            
            const qboInvoice = qboDataPayload?.invoices?.find((inv: any) => String(inv.id) === String(app.invoice_qbo_id) && inv.success);
            if (!qboInvoice?.data) return false;
            
            // Check if applied amounts match
            const eservAppliedCents = Number(app.applied_amount || 0);
            const qboAppliedAmount = qboPaymentData.Line?.reduce((sum: any, line: any) => {
              if (line.LinkedTxn?.some((link: any) => link.TxnId === app.invoice_qbo_id)) {
                return sum + (Number(line.Amount) || 0);
              }
              return sum;
            }, 0) || 0;
            const qboAppliedCents = Math.round(qboAppliedAmount * 100);
            
            return eservAppliedCents !== qboAppliedCents;
          }) || false;
        }

        // Check customer name mismatch
        if (!hasMismatch && item.customer_qbo_id) {
          const qboCustomer = qboDataPayload?.customers?.find((c: any) => String(c.id) === String(item.customer_qbo_id) && c.success);
          if (qboCustomer?.data) {
            const eservCustomerName = item.company_name || `${item.first_name || ''} ${item.last_name || ''}`.trim();
            const qboCustomerName = qboCustomer?.data?.DisplayName || '';
            if (eservCustomerName && qboCustomerName && eservCustomerName !== qboCustomerName) {
              hasMismatch = true;
            }
          }
        }
        
        if (!hasMismatch) return false;
      }

      return true;
    });
  };

  // Add sync invoice functionality
  const syncInvoice = async (invoiceId: number) => {
    setSyncingInvoices(prev => new Set(prev).add(invoiceId));
    setBanner(null); // Clear any existing banner
    
    try {
      const result = await syncInvoiceToQbo(invoiceId);
      
      if (result.success) {
        // Update the specific invoice in the items array with the new QBO ID
        setItems(prevItems => 
          prevItems.map(item => ({
            ...item,
            applications: item.applications?.map(app => 
              app.invoice_id === invoiceId 
                ? { ...app, invoice_qbo_id: result.data?.qbol_id || result.data?.qb_id, can_correct: true }
                : app
            )
          }))
        );
        
        // Refresh QBO details to get updated invoice information
        try {
          const selectedItems = items.filter(item => selectedIds.has(item.payment_id));
          if (selectedItems.length > 0) {
            const paymentQboIds = selectedItems
              .map(item => item.payment_qbo_id)
              .filter(Boolean) as string[];
            
            const invoiceQboIds = Array.from(new Set(
              selectedItems.flatMap(item => 
                (item.applications || [])
                  .map(app => app.invoice_qbo_id)
                  .filter(Boolean)
              )
            )) as string[];

            const customerQboIds = Array.from(new Set(
              selectedItems
                .map(item => item.customer_qbo_id)
                .filter(Boolean)
            )) as string[];

            if (paymentQboIds.length > 0 || customerQboIds.length > 0) {
              const updatedQboData = await fetchAppliedPaymentsQboDetails(paymentQboIds, invoiceQboIds, customerQboIds);
              setQbo(updatedQboData);
            }
          }
        } catch (refreshError) {
          console.error('Failed to refresh QBO details after sync:', refreshError);
        }
        
        setBanner({ type: 'success', text: `Invoice ${invoiceId} synced to QuickBooks successfully (QBO ID: ${result.data?.qbol_id || result.data?.qb_id})` });
      } else {
        throw new Error(result.error || 'Sync failed');
      }
    } catch (e: any) {
      console.error('Failed to sync invoice:', e);
      setBanner({ type: 'error', text: `Failed to sync invoice ${invoiceId}: ${e.message || 'Unknown error'}` });
    } finally {
      setSyncingInvoices(prev => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  // Apply payment to invoice in QBO
  const applyPayment = async (paymentId: number, invoiceId: number, qbolInvoiceId: string, qbolPaymentId: string, amount: number) => {
    const applyKey = `${paymentId}-${invoiceId}`;
    setApplyingPayments(prev => new Set(prev).add(applyKey));
    setBanner(null);
    
    try {
      const request: ApplyPaymentRequest = {
        invoiceId,
        paymentId,
        qbolInvoiceId,
        qbolPaymentId,
        amount
      };

      const result = await applyPaymentToInvoice(request);
      
      if (result.action === 'Linked Payment') {
        setBanner({ type: 'success', text: `Payment applied successfully: ${result.reason}` });
        
        // Refresh QBO details to show updated payment applications
        try {
          const selectedItems = items.filter(item => selectedIds.has(item.payment_id));
          if (selectedItems.length > 0) {
            const paymentQboIds = selectedItems
              .map(item => item.payment_qbo_id)
              .filter(Boolean) as string[];
            
            const invoiceQboIds = Array.from(new Set(
              selectedItems.flatMap(item => 
                (item.applications || [])
                  .map(app => app.invoice_qbo_id)
                  .filter(Boolean)
              )
            )) as string[];

            const customerQboIds = Array.from(new Set(
              selectedItems
                .map(item => item.customer_qbo_id)
                .filter(Boolean)
            )) as string[];

          if (paymentQboIds.length > 0 || customerQboIds.length > 0) {
              const updatedQboData = await fetchAppliedPaymentsQboDetails(paymentQboIds, invoiceQboIds, customerQboIds);
              setQbo(updatedQboData);
            }
          }
        } catch (refreshError) {
          console.error('Failed to refresh QBO details after apply payment:', refreshError);
        }

        await refreshCurrentPaymentWithQbo(currentPayment?.payment_id ?? null);
      } else if (result.action === 'Skipped') {
        setBanner({ type: 'info', text: `Payment application skipped: ${result.reason}` });
      } else {
        setBanner({ type: 'error', text: `Failed to apply payment: ${result.reason}` });
      }
    } catch (e: any) {
      console.error('Failed to apply payment:', e);
      setBanner({ type: 'error', text: `Failed to apply payment: ${e.message || 'Unknown error'}` });
    } finally {
      setApplyingPayments(prev => {
        const next = new Set(prev);
        next.delete(applyKey);
        return next;
      });
    }
  };

  // Sync payment to QuickBooks
  const resyncPayment = async (paymentId: number) => {
    setResyncingPayments(prev => new Set(prev).add(paymentId));
    setBanner(null);
    
    try {
      // First, check if we need to sync dependencies
      const payment = items.find(i => i.payment_id === paymentId);
      if (!payment) {
        throw new Error('Payment not found');
      }

      // Check if invoices need syncing first
      const unsyncedInvoices = (payment.applications || []).filter(app => !app.invoice_qbo_id);
      if (unsyncedInvoices.length > 0) {
        const syncInvoices = window.confirm(
          `${unsyncedInvoices.length} invoice(s) are not synced to QuickBooks. Do you want to sync them first?\n\nInvoices: ${unsyncedInvoices.map(inv => `#${inv.invoice_id}`).join(', ')}`
        );
        if (syncInvoices) {
          setBanner({ type: 'info', text: 'Syncing invoices to QuickBooks...' });
          // Sync each invoice
          for (const app of unsyncedInvoices) {
            try {
              await syncInvoiceForReconciliation(app.invoice_id);
            } catch (error) {
              console.error(`Failed to sync invoice ${app.invoice_id}:`, error);
            }
          }
        }
      }

      // Now sync the payment using new reconciliation sync module
      setBanner({ type: 'info', text: 'Syncing payment to QuickBooks...' });
      const result = await syncPaymentForReconciliation(paymentId);
      
      if (DEBUG_LOGGING) console.log('Resync payment result:', result);
      
      if (result.success) {
        setBanner({ type: 'success', text: `Successfully synced payment ${paymentId} to QuickBooks (QBO ID: ${result.qboId})` });
        // Refresh all data to show updated QBO information
        await refreshData();
      } else {
        setBanner({ type: 'error', text: `Failed to sync payment: ${result.error || result.message}` });
      }
    } catch (error: any) {
      console.error('Error resyncing payment:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unknown error';
      setBanner({ type: 'error', text: `Failed to sync payment: ${errorMessage}` });
    } finally {
      setResyncingPayments(prev => {
        const next = new Set(prev);
        next.delete(paymentId);
        return next;
      });
    }
  };

  // Activate existing inactive customer in QBO
  const activateCustomer = async (customerId: number) => {
    const syncKey = `customer-${customerId}`;
    setSyncingItems(prev => new Set(prev).add(syncKey));
    setBanner({ type: 'info', text: 'Activating customer in QuickBooks...' });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/sync/customer/activate`,
        { customerId }
      );
      
      if (response.data.success) {
        setBanner({ type: 'success', text: response.data.message });
        
        // Refresh the current payment data to reflect the change
        const refreshResult = await refreshCurrentPaymentWithQbo(currentPayment?.payment_id ?? null);
        if (refreshResult?.payment) {
          setCurrentPayment(refreshResult.payment);
        }
      } else {
        throw new Error(response.data.message || 'Failed to activate customer');
      }
    } catch (error: any) {
      console.error('Error activating customer:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unknown error';
      setBanner({ type: 'error', text: `Failed to activate customer: ${errorMessage}` });
    } finally {
      setSyncingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncKey);
        return newSet;
      });
    }
  };

  // Sync customer to QuickBooks using new reconciliation sync module
  const resyncCustomer = async (customerId: number) => {
    setResyncingCustomers(prev => new Set(prev).add(customerId));
    setBanner(null);
    setCustomerStatuses(prev => ({
      ...prev,
      [customerId]: { type: 'info', text: 'Syncing customer to QuickBooks...' }
    }));
    setBanner({ type: 'info', text: 'Syncing customer to QuickBooks...' });

    try {
      const result = await syncCustomerForReconciliation(customerId);
      
      if (DEBUG_LOGGING) console.log('Resync customer result:', result);

      if (result.success) {
        const newQboId = result.qboId || result.data?.qboId;
        const statusText = result.message
          || (newQboId
            ? `Customer synced to QuickBooks (QBO ID: ${newQboId})`
            : 'Customer synced to QuickBooks');

        setCustomerStatuses(prev => ({
          ...prev,
          [customerId]: {
            type: 'success',
            text: statusText,
          }
        }));

        setBanner({ type: 'success', text: statusText });

        // Update local state immediately so UI reflects the new QBO ID
        const nextItems = items.map(item => {
          if (item.fk_bcustomer_id !== customerId) return item;
          return {
            ...item,
            customer_qbo_id: newQboId ?? item.customer_qbo_id,
            customer_qbol_message: result.message,
          };
        });
        setItems(nextItems);

        // Refresh QBO details for currently selected payments to show new linkage
        const selectedItems = nextItems.filter(item => selectedIds.has(item.payment_id));
        if (selectedItems.length > 0) {
          const qboDetails = await fetchQboDataForItems(selectedItems);
          // Update the qbo state so the UI reflects the changes
          if (qboDetails) {
            setQbo(qboDetails);
          }
        }

        // Also refresh current payment if it's the one being synced
        if (currentPayment && currentPayment.fk_bcustomer_id === customerId) {
          const updatedCurrentPayment = nextItems.find(item => item.payment_id === currentPayment.payment_id);
          if (updatedCurrentPayment) {
            setCurrentPayment(updatedCurrentPayment);
            // Refresh QBO data for the current payment
            const qboDetails = await fetchQboDataForItems([updatedCurrentPayment]);
            if (qboDetails) {
              setQbo(qboDetails);
            }
          }
        }
      } else {
        const errorText = result.error || result.message || 'Failed to sync customer';
        setCustomerStatuses(prev => ({
          ...prev,
          [customerId]: { type: 'error', text: errorText }
        }));
        setBanner({ type: 'error', text: errorText });
      }
    } catch (error: any) {
      console.error('Error resyncing customer:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unknown error';
      setBanner({ type: 'error', text: `Failed to sync customer: ${errorMessage}` });
      setCustomerStatuses(prev => ({
        ...prev,
        [customerId]: { type: 'error', text: `Failed to sync customer: ${errorMessage}` }
      }));
    } finally {
      setResyncingCustomers(prev => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    }
  };


  // Crawl tool functions
  const fetchCrawlTotals = async () => {
    setCrawlStatus('fetching_totals');
    setQboConnectionStatus('checking');
    setError(null);
    
    try {
      // Fetch EServ totals using existing local summary endpoint
      const eservResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/local-summary`,
        {
          params: {
            startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last year
            endDate: new Date().toISOString().split('T')[0]
          }
        }
      );

      const eservData = eservResponse.data.data;
      
      // Try to get QBO totals using a simple query approach
      let qboData = null;
      try {
        // First check if we can connect to QBO
        const qboResponse = await pingQboCompanyLegacy();
        if (qboResponse) {
          setQboConnectionStatus('connected');
          // Try to get QBO totals by making direct API calls
          qboData = await fetchQboTotals();
        } else {
          setQboConnectionStatus('disconnected');
        }
      } catch (qboError) {
        console.warn('Failed to fetch QBO totals:', qboError);
        setQboConnectionStatus('error');
        setBanner({ type: 'error', text: 'Failed to fetch QBO data. Please check your QuickBooks connection.' });
      }

      setCrawlTotals({
        eserv: {
          totalInvoices: eservData.invoices.total_invoices || 0,
          paidInvoices: eservData.invoices.synced_to_qbo || 0,
          unpaidInvoices: eservData.invoices.not_synced_to_qbo || 0,
          totalPayments: eservData.payments?.total_payments || 0,
          totalAmount: eservData.payments?.total_amount || 0,
          totalCustomers: eservData.customers?.total_customers || 0
        },
        qbo: qboData
      });

      setCrawlStatus('ready');
    } catch (error: any) {
      console.error('Failed to fetch crawl totals:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to fetch totals');
      setCrawlStatus('idle');
      setQboConnectionStatus('error');
    } finally {
      // Loading state handled by crawlStatus
    }
  };

  // Helper function to fetch QBO totals
  const fetchQboTotals = async () => {
    try {
      // Use the QBO totals endpoint
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/qbo-totals`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch QBO totals');
      }
    } catch (error) {
      logQboError('fetchQboTotals', error);
      // Return placeholder data if the endpoint fails
      return {
        totalInvoices: 0,
        paidInvoices: 0,
        unpaidInvoices: 0,
        totalPayments: 0,
        totalAmount: 0,
        totalCustomers: 0,
        note: 'QBO totals unavailable - check connection'
      };
    }
  };


  const startCrawl = async () => {
    setCrawlStatus('crawling');
    setCrawlStarted(true);
    setIsDataComparisonCollapsed(true); // Auto-collapse data comparison when crawl starts
    setAuditLog([]); // Reset audit log for new crawl
    processedCountRef.current = 0; // Reset processed count
    setBanner({ type: 'info', text: 'Crawl starting...' });
    
    // If we already have a current payment (from jump), start from there
    if (currentPayment && startPaymentId) {
      if (DEBUG_LOGGING) console.log('🚀 Starting crawl from current payment (jumped to):', currentPayment.payment_id);
      setCurrentPage(1); // Start from page 1 when crawling from jumped payment
      setCrawlProgress({
        current: 1,
        total: 0, // Will be set when we get the first payment
        processed: 0,
        reconciled: 0,
        needsFix: 0
      });
      
      // Evaluate the current payment and start crawling from there
      const qboData = await fetchQboDataForItems([currentPayment]);
      if (currentPayment.payment_qbo_id && !qboData) {
        setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${currentPayment.payment_id}.` });
      }
      evaluateCrawlPayment(currentPayment, qboData);
      
      const startNote = `Crawl started from payment ID ${startPaymentId}`;
      logAuditEntry('started', currentPayment, startNote);
      return;
    }
    
    // Otherwise, start from the beginning or from startPaymentId
    setCurrentPage(1); // Reset to first page
    clearCrawlAutoAdvance();
    setCrawlUiState('loading');
    setCrawlProgress({
      current: 1,
      total: 0, // Will be set when we get the first payment
      processed: 0,
      reconciled: 0,
      needsFix: 0
    });

    try {
      // Get the first payment to process
      // If startPaymentId is provided, we'll fetch payments starting from that ID
      let searchParam = '';
      if (startPaymentId) {
        searchParam = startPaymentId;
      }
      
      const response = await getAppliedPaymentCandidates(1, 1, searchParam);
      if (response.items && response.items.length > 0) {
        const firstPayment = response.items[0];
        setCurrentPayment(firstPayment);
        setCrawlProgress(prev => prev ? { ...prev, total: response.total } : {
          current: 1,
          total: response.total,
          processed: 0,
          reconciled: 0,
          needsFix: 0
        });

        const qboData = await fetchQboDataForItems([firstPayment]);
        if (firstPayment.payment_qbo_id && !qboData) {
          setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${firstPayment.payment_id}.` });
        }
        evaluateCrawlPayment(firstPayment, qboData);
        
        const startNote = startPaymentId ? `Crawl started from payment ID ${startPaymentId}` : 'Crawl started';
        logAuditEntry('started', firstPayment, startNote);
      } else {
        setCrawlStatus('completed');
        const noPaymentsMsg = startPaymentId 
          ? `No payments found starting from ID ${startPaymentId}` 
          : 'No payments found to crawl';
        setBanner({ type: 'info', text: noPaymentsMsg });
        setCrawlUiState('idle');
      }
    } catch (error: any) {
      console.error('Failed to start crawl:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to start crawl');
      setCrawlStatus('ready');
      setCrawlUiState('ready');
    }
  };

  const markPaymentAsProcessed = async (auto = false) => {
    if (!currentPayment) return;

    clearCrawlAutoAdvance();
    setCrawlAdvanceCountdown(null);

    // IMMEDIATE CHECK BEFORE LOGGING: If processed count >= original payment count, STOP
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP in markPaymentAsProcessed before logging: Already at limit - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }

    const auditNote = auto
      ? 'Payment auto-processed after reconciliation check'
      : 'Payment marked as successfully reconciled';
    logAuditEntry('processed', currentPayment, auditNote);

    if (!auto) {
      setBanner({ type: 'success', text: `Payment ${currentPayment.payment_id} marked as processed.` });
    }

    // IMMEDIATE CHECK AFTER LOGGING: If processed count >= original payment count, STOP
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP in markPaymentAsProcessed after logging: Processed count >= total - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }

    // Check if we're at or past the limit before advancing
    if (crawlProgress && crawlProgress.current >= crawlProgress.total) {
      if (DEBUG_LOGGING) console.log('🚨 Mark as Processed but at limit - completing crawl');
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${crawlProgress.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }

    // Always advance to next payment after marking as processed
    if (DEBUG_LOGGING) console.log('🔄 Mark as Processed - advancing to next payment');
    setCrawlUiState('loading');
    await goToNextPayment();
  };

  const markPaymentAsNeedsFix = async () => {
    if (!currentPayment) return;

    clearCrawlAutoAdvance();
    setCrawlAdvanceCountdown(null);

    // IMMEDIATE CHECK BEFORE LOGGING: If processed count >= original payment count, STOP
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP in markPaymentAsNeedsFix before logging: Already at limit - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }

    // Log audit entry
    logAuditEntry('needs_fix', currentPayment, 'Payment marked as needing reconciliation fix');

    setBanner({ type: 'error', text: `Payment ${currentPayment.payment_id} marked as needing fix.` });
    
    // IMMEDIATE CHECK AFTER LOGGING: If processed count >= original payment count, STOP
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP in markPaymentAsNeedsFix after logging: Processed count >= total - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }
    
    // Check if we're at or past the limit before advancing
    if (crawlProgress && crawlProgress.current >= crawlProgress.total) {
      if (DEBUG_LOGGING) console.log('🚨 Mark as Needs Fix but at limit - completing crawl');
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${crawlProgress.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }
    
    // Always advance to next payment after marking as needs fix
    if (DEBUG_LOGGING) console.log('🔴 Mark as Needs Fix - advancing to next payment');
    setCrawlUiState('loading');
    await goToNextPayment();
  };

  const moveToNextPayment = async (auto = false, manual = false) => {
    if (!crawlProgress || !currentPayment) return;

    clearCrawlAutoAdvance();
    setCrawlUiState('loading');

    try {
      const nextPage = currentPage + 1;
      // After starting with a specific payment ID, continue with normal pagination
      const searchParam = '';
      const response = await getAppliedPaymentCandidates(nextPage, 1, searchParam);

      if (response.items && response.items.length > 0) {
        const nextPayment = response.items[0];
        setCurrentPayment(nextPayment);
        setCurrentPage(nextPage);
        setCrawlProgress(prev => prev ? { ...prev, current: Math.min(prev.total, prev.current + 1) } : {
          current: Math.min(nextPage, 1),
          total: 1,
          processed: 0,
          reconciled: 0,
          needsFix: 0
        });

        if (!auto && !manual) {
          setBanner({ type: 'info', text: `Payment ${nextPayment.payment_id} loaded. Review and mark as processed or needing fix.` });
        }

        const qboData = await fetchQboDataForItems([nextPayment]);
        if (nextPayment.payment_qbo_id && !qboData && !auto && !manual) {
          setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${nextPayment.payment_id}.` });
        }

        evaluateCrawlPayment(nextPayment, qboData);
      } else {
        logAuditEntry('completed', currentPayment, 'Crawl completed - no more payments to process');
        setCrawlStatus('completed');
        setCrawlUiState('idle');
        setBanner({ type: 'success', text: 'Crawl completed! All payments processed.' });
      }
    } catch (error: any) {
      console.error('Failed to get next payment:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to get next payment');
      setCrawlUiState('ready');
    }
  };

  const goToPreviousPayment = async () => {
    if (!crawlProgress || !currentPayment || currentPage <= 1) {
      return;
    }

    clearCrawlAutoAdvance();
    setCrawlUiState('loading');

    try {
      const prevPage = currentPage - 1;
      // Use normal pagination for previous payments
      const response = await getAppliedPaymentCandidates(prevPage, 1, '');

      if (response.items && response.items.length > 0) {
        const previousPayment = response.items[0];
        setCurrentPayment(previousPayment);
        setCurrentPage(prevPage);
        setCrawlProgress(prev => prev ? { ...prev, current: Math.max(1, prevPage) } : prev);

        if (crawlStatus === 'crawling') {
          setBanner({ type: 'info', text: `Payment ${previousPayment.payment_id} loaded.` });
        }

        if (crawlStatus === 'crawling') {
          const qboData = await fetchQboDataForItems([previousPayment]);
          if (previousPayment.payment_qbo_id && !qboData) {
            setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${previousPayment.payment_id}.` });
          }
          evaluateCrawlPayment(previousPayment, qboData);
        } else {
          setCrawlUiState('ready');
        }
      }
    } catch (error: any) {
      console.error('Failed to load previous payment:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to load previous payment');
      setCrawlUiState('ready');
    }
  };

  const goToNextPaymentManually = async () => {
    // IMMEDIATE CHECK: If processed count >= original payment count, STOP EVERYTHING
    if (crawlProgress && processedCountRef.current >= crawlProgress.total && crawlProgress.total > 0) {
      if (DEBUG_LOGGING) console.log('🚨 STOP: Manual advance blocked - processed count >= original payment count - processed:', processedCountRef.current, 'total:', crawlProgress.total);
      setCrawlStatus('completed');
      setBanner({ type: 'info', text: `Crawl completed! Processed ${processedCountRef.current} of ${crawlProgress.total} payments.` });
      setCrawlUiState('idle');
      setCurrentPayment(null);
      return;
    }
    
    await moveToNextPayment(false, true);
  };

  const pauseCrawl = () => {
    // If there's an active countdown, just pause auto-advance
    if (crawlAdvanceCountdown !== null) {
      setIsAutoAdvancePaused(true);
      clearCrawlAutoAdvance();
      setCrawlAdvanceCountdown(null);
      setBanner({ type: 'info', text: 'Auto-advance paused. Click "Resume" to continue countdown.' });
    } else {
      // Otherwise, pause the entire crawl
      setCrawlStatus('paused');
      setIsAutoAdvancePaused(true);
      clearCrawlAutoAdvance();
      setCrawlAdvanceCountdown(null);
      if (crawlUiState === 'matched' || crawlUiState === 'loading') {
        setCrawlUiState('ready');
      }
      setBanner({ type: 'info', text: 'Crawl paused. Click "Continue" to resume.' });
    }
  };

  const resumeCrawl = () => {
    if (DEBUG_LOGGING) console.log('🔄 ResumeCrawl called - current payment:', currentPayment?.payment_id, 'current page:', currentPage);
    setCrawlStatus('crawling');
    setIsAutoAdvancePaused(false);
    setBanner({ type: 'info', text: 'Crawl resumed. Advancing to next payment...' });
    
    // Advance to next payment instead of re-evaluating current one
    setCrawlUiState('loading');
    if (DEBUG_LOGGING) console.log('🔄 ResumeCrawl - calling goToNextPayment');
    goToNextPayment();
  };

  const toggleAutoAdvance = () => {
    if (isAutoAdvancePaused) {
      setIsAutoAdvancePaused(false);
      setBanner({ type: 'info', text: 'Auto-advance resumed. Countdown will continue.' });
      // If we're in matched state, restart the countdown
      if (crawlUiState === 'matched' && currentPayment && qbo) {
        startCountdown();
      }
    } else {
      setIsAutoAdvancePaused(true);
      clearCrawlAutoAdvance();
      setCrawlAdvanceCountdown(null);
      setBanner({ type: 'info', text: 'Auto-advance paused. Click "Resume" to continue countdown.' });
    }
  };

  const resetCrawl = () => {
    setCrawlStatus('idle');
    setCrawlStarted(false);
    setCurrentPayment(null);
    setCrawlProgress(null);
    setCurrentPage(1);
    setBanner(null);
    clearCrawlAutoAdvance();
    setCrawlAdvanceCountdown(null);
    setIsAutoAdvancePaused(false);
    setStopOnMismatch(true); // Reset to default
    setCrawlUiState('idle');
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Tab Navigation */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white">
        <nav className="flex space-x-8 px-6 py-3" aria-label="Tabs">
          {/* <button
            onClick={() => setActiveTab('investigate')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'investigate'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Investigate
          </button> */}
          <button
            onClick={() => setActiveTab('crawl')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'crawl'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Crawl
          </button>
          <button
            onClick={() => setActiveTab('dev')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dev'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Dev
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {/* {activeTab === 'investigate' && (
        <> */}
      {false && (
        <>
          {/* Fixed Header - Filters and Summary */}
          <div className="flex-shrink-0 space-y-2 p-3 border-b border-gray-200 bg-white">
          {/* Summary Section */}
          {summaryData && (
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="flex items-center justify-between">
                {/* Local Data */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-xs font-medium text-gray-700">EServ Local:</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <div><span className="text-gray-500">Scan:</span> <span className="font-semibold text-gray-900">{summaryData.local.invoicesToScan.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Mismatches:</span> <span className="font-semibold text-red-600">{summaryData.local.mismatches.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Last Run:</span> <span className="font-semibold text-gray-900">{summaryData.local.lastRun ? formatDate(summaryData.local.lastRun) : 'Never'}</span></div>
                  </div>
                </div>
                {/* QBO Data */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs font-medium text-gray-700">QuickBooks Online:</span>
                    {summaryLoading && (<span className="h-1.5 w-1.5 animate-spin rounded-full border-b-2 border-green-600 ml-1"></span>)}
                  </div>
                  {summaryData.qbo ? (
                    <div className="flex items-center gap-3 text-xs">
                      <div><span className="text-gray-500">Invoices:</span> <span className="font-semibold text-gray-900">{summaryData.qbo.totalInvoices.toLocaleString()}</span></div>
                      <div><span className="text-gray-500">Paid:</span> <span className="font-semibold text-green-600">{summaryData.qbo.paidInFull.toLocaleString()}</span></div>
                      <div><span className="text-gray-500">Payments:</span> <span className="font-semibold text-gray-900">{summaryData.qbo.totalPayments.toLocaleString()}</span></div>
                      <div><span className="text-gray-500">Amount:</span> <span className="font-semibold text-gray-900">{formatMoney(summaryData.qbo.totalAmount)}</span></div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">{connectionStatus ? 'Loading QBO data...' : 'QBO not connected'}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-medium text-gray-700">Filter Tools</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {getFilteredItems(items).length} of {items.length} payments
                {selectedIds.size > 0 && (
                  <span className="ml-2 text-orange-600">
                    · {selectedIds.size} selected
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              {/* Search Input */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-600">Search</label>
                <input
                  type="text"
                  placeholder="Payment ID, amount, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              {/* Missing in QBO Filter */}
              <div className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  id="missing-in-qbo"
                  checked={showMissingInQbo}
                  onChange={(e) => setShowMissingInQbo(e.target.checked)}
                  disabled={!qbo}
                  className="h-3 w-3 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="missing-in-qbo" className={`text-xs ${!qbo ? 'text-gray-400' : 'text-gray-700'}`}>
                  Missing in QBs
                </label>
              </div>
              
              {/* Mismatch Filter */}
              <div className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  id="mismatch"
                  checked={showMismatch}
                  onChange={(e) => setShowMismatch(e.target.checked)}
                  disabled={!qbo}
                  className="h-3 w-3 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="mismatch" className={`text-xs ${!qbo ? 'text-gray-400' : 'text-gray-700'}`}>
                  Mismatch EServ vs QBs
                </label>
              </div>

              {/* Page Size Dropdown */}
              <div className="flex items-center gap-1">
                <label className="text-xs font-medium text-gray-600">Per page:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const newPageSize = parseInt(e.target.value);
                    setPageSize(newPageSize);
                    loadPage(1, newPageSize, searchTerm);
                  }}
                  className="px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Action Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button 
                  type="button" 
                  onClick={() => setSelectedIds(new Set(getFilteredItems(items).map(i => i.payment_id)))} 
                  className="px-1 py-0.5 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50" 
                  disabled={getFilteredItems(items).length === 0}
                >
                  Select all filtered
                </button>
                <button 
                  type="button" 
                  onClick={() => setSelectedIds(new Set())} 
                  className="px-1 py-0.5 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50" 
                  disabled={selectedIds.size === 0}
                >
                  Select none
                </button>
                {(searchTerm || showMissingInQbo || showMismatch) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setShowMissingInQbo(false);
                      setShowMismatch(false);
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Fetch QBO Data Button */}
                {selectedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={fetchQboDataForSelected}
                    disabled={qboLoading}
                    className="px-1 py-0.5 rounded bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {qboLoading ? 'Loading...' : `Fetch QBO (${selectedIds.size})`}
                  </button>
                )}
                
                {/* Connection Status */}
                {connectionStatus && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Connected
                  </span>
                )}
                {!connectionStatus && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    <svg className="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Failed
                  </span>
                )}
              </div>
            </div>
          </div>


      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-auto p-2">
        <div className="space-y-2">

          {/* Banner Messages */}
          {banner && (
            <div className={`${banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : banner.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'} border rounded-md p-2 text-sm`}>
              {banner.text}
            </div>
          )}

          {/* Loading QBO Data Indicator */}
          {qboLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-spin rounded-full border-b-2 border-blue-600"></span>
                Loading QuickBooks data for selected payments...
              </div>
            </div>
          )}

          {/* Empty States */}
          {getFilteredItems(items).length === 0 && items.length > 0 && (
            <div className="text-sm text-gray-600 text-center py-4">
              No payments match the current filters.
            </div>
          )}
          
          {items.length === 0 && <div className="text-sm text-gray-600">No eligible payments found.</div>}

          {/* Payment List with EServ and QBO Data */}
          {getFilteredItems(items).map(it => {
            const isSelected = selectedIds.has(it.payment_id);
            const qboDataPayload: any = qboPayload;
            const qboPay = qboDataPayload?.payments?.find((p: any) => String(p.id) === String(it.payment_qbo_id));
            const qboPayment = qboPay && qboPay.success ? qboPay.data : null;
            const qboPaymentError = qboPay && !qboPay.success ? extractQboFaultMessage(qboPay.error) || qboPay.error : null;

            // Check if payment exists in QBO but data is missing (out of sync)
            const isPaymentOutOfSync = it.payment_qbo_id && !qboPayment;
            const isPaymentNotFound = it.payment_qbo_id && qboPay && !qboPay.success;

            // Debug QBO payment lookup
            if (DEBUG_LOGGING) console.log('QBO Payment Lookup Debug:', {
              paymentId: it.payment_id,
              paymentQboId: it.payment_qbo_id,
              qboData: qboDataPayload,
              qboPayments: qboDataPayload?.payments,
              qboPaymentsLength: qboDataPayload?.payments?.length,
              qboPay: qboPay,
              qboPayment: qboPayment,
              qboPaymentTotalAmt: qboPayment?.TotalAmt,
              qboPaymentUnappliedAmt: qboPayment?.UnappliedAmt,
              searchForId: String(it.payment_qbo_id),
              availableIds: (qboDataPayload?.payments || []).map((p: any) => String(p.id))
            });

            // Calculate applied amount from Line items instead of TotalAmt - UnappliedAmt
            const qboApplied = qboPayment ?
              (qboPayment.Line || []).reduce((sum, line) => {
                // Only count lines that have LinkedTxn (applied to invoices)
                if (line.LinkedTxn && line.LinkedTxn.length > 0) {
                  return sum + (Number(line.Amount) || 0);
                }
                return sum;
              }, 0) : 0; // Show 0 if no payment data, not null

            // Debug QBO payment data
            if (DEBUG_LOGGING) console.log('QBO Payment Debug:', {
              paymentId: it.payment_id,
              qboPaymentId: it.payment_qbo_id,
              qboData: qboDataPayload,
              qboPayments: qboDataPayload?.payments,
              qboPay: qboPay,
              qboPayment: qboPayment,
              totalAmt: qboPayment?.TotalAmt,
              unappliedAmt: qboPayment?.UnappliedAmt,
              qboApplied: qboApplied,
              lineItems: qboPayment?.Line,
              lineItemCount: qboPayment?.Line?.length || 0,
              calculatedApplied: qboPayment ? (qboPayment.Line || []).reduce((sum, line) => {
                if (line.LinkedTxn && line.LinkedTxn.length > 0) {
                  return sum + (Number(line.Amount) || 0);
                }
                return sum;
              }, 0) : 0,
              hasLinkedTransactions: qboPayment ? (qboPayment.Line || []).some(line => line.LinkedTxn && line.LinkedTxn.length > 0) : false
            });

            const invoiceMap = new Map<string, any>();
            (qboDataPayload?.invoices || []).forEach((inv: any) => invoiceMap.set(String(inv.id), inv));
            const customerMap = new Map<string, any>();
            (qboDataPayload?.customers || []).forEach((cust: any) => customerMap.set(String(cust.id), cust));
            const qboCustomer = it.customer_qbo_id ? customerMap.get(String(it.customer_qbo_id)) : null;
            const qboCustomerData = qboCustomer?.data;
            const qboCustomerMissing = it.customer_qbo_id && (!qboCustomer || !qboCustomer.success || !qboCustomerData);

            const qboTotalCents = qboPayment ? Math.round((Number(qboPayment.TotalAmt) || 0) * 100) : null;
            const qboAppliedCents = qboPayment ? Math.round(qboApplied * 100) : null;
            const qboRemainingCents = qboPayment ? Math.round((Number(qboPayment.UnappliedAmt) || 0) * 100) : null;

            let paymentStatusLabel = it.payment_qbo_id ? 'Synced' : 'Not synced';
            let paymentStatusClasses = it.payment_qbo_id
              ? 'border border-blue-200 bg-blue-50 text-blue-700'
              : 'border border-gray-200 bg-gray-100 text-gray-700';

            if (isPaymentNotFound || isPaymentOutOfSync) {
              paymentStatusLabel = 'Missing in QBO';
              paymentStatusClasses = 'border border-red-200 bg-red-50 text-red-700';
            } else if (qboPayment) {
              if (qboPayment.UnappliedAmt === 0 && qboPayment.TotalAmt > 0) {
                paymentStatusLabel = 'Fully applied';
                paymentStatusClasses = 'border border-green-200 bg-green-50 text-green-700';
              } else if (qboPayment.TotalAmt === 0) {
                paymentStatusLabel = 'Zero amount';
                paymentStatusClasses = 'border border-gray-200 bg-gray-100 text-gray-700';
              } else if (qboApplied > 0) {
                paymentStatusLabel = 'Partially applied';
                paymentStatusClasses = 'border border-yellow-200 bg-yellow-50 text-yellow-700';
              } else {
                paymentStatusLabel = 'Unapplied';
                paymentStatusClasses = 'border border-red-200 bg-red-50 text-red-700';
              }
            }

            const paymentMetrics: Array<{ label: string; local: React.ReactNode; remote: React.ReactNode }> = [
              {
                label: 'Payment Amount',
                local: <span className="font-medium text-gray-900">{formatMoney(it.payment_amount as any)}</span>,
                remote: <span className="font-medium text-gray-900">{qboTotalCents != null ? formatMoney(qboTotalCents) : '—'}</span>
              },
              {
                label: 'Applied Total',
                local: <span className="font-medium text-gray-900">{formatMoney(it.applied_total as any)}</span>,
                remote: <span className="font-medium text-gray-900">{qboAppliedCents != null ? formatMoney(qboAppliedCents) : '—'}</span>
              },
              {
                label: 'Remaining',
                local: <span className="font-medium text-gray-900">{formatMoney((it.payment_amount as any) - (it.applied_total as any))}</span>,
                remote: <span className="font-medium text-gray-900">{qboRemainingCents != null ? formatMoney(qboRemainingCents) : '—'}</span>
              },
              {
                label: 'Sync Status',
                local: <span className="text-gray-700">{it.payment_qbo_id ? 'Synced' : 'Not synced'}</span>,
                remote: (
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium " + paymentStatusClasses}>
                        {paymentStatusLabel}
                      </span>
                      {it.payment_qbo_id && qboPayment && !isPaymentNotFound && !isPaymentOutOfSync && (
                        <button
                          onClick={() => resyncPayment(it.payment_id)}
                          disabled={resyncingPayments.has(it.payment_id)}
                          className="rounded border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {resyncingPayments.has(it.payment_id) ? 'Syncing…' : 'Sync Payment'}
                        </button>
                      )}
                    </div>
                    {qboPaymentError && (
                      <span className="text-[11px] text-red-600">{stringifyError(qboPaymentError)}</span>
                    )}
                  </div>
                )
              }
            ];

            const localCustomerContent = (
              <div className="space-y-0.5">
                <div className="font-medium text-gray-900">{it.company_name || `${it.first_name ?? ''} ${it.last_name ?? ''}` || '—'}</div>
                <div className="text-xs text-gray-500">QBO ID: {it.customer_qbo_id ?? '—'}</div>
                {it.customer_qbol_message && (
                  <div className="text-xs text-orange-600">{it.customer_qbol_message}</div>
                )}
              </div>
            );

            const customerStatus = it.fk_bcustomer_id ? customerStatuses[it.fk_bcustomer_id] : undefined;
            const customerStatusClass = customerStatus?.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : customerStatus?.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-blue-200 bg-blue-50 text-blue-700';
            const isCustomerSyncing = it.fk_bcustomer_id != null && resyncingCustomers.has(it.fk_bcustomer_id);
            // Determine customer button label based on state
            const getCustomerButtonLabel = () => {
              if (isCustomerSyncing) return 'Syncing…';
              
              // Check if customer is inactive in QBO
              const eservCustomerQboId = it.customer_qbo_id ? String(it.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
              const qboCustomer = qbo?.customers?.find((c: any) => 
                c.success && c.data && digitsOnly(c.data.Id) === digitsOnly(eservCustomerQboId)
              );
              const isInactive = qboCustomer?.isActive === false;
              
              if (isInactive) return 'Resync as New';
              if (it.customer_qbo_id) return 'Sync Customer';
              return 'Sync Customer to QBO';
            };
            
            const customerButtonLabel = getCustomerButtonLabel();
            // Check if customer is missing or inactive
            const eservCustomerQboId = it.customer_qbo_id ? String(it.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
            const qboCustomerForMissing = qbo?.customers?.find((c: any) => 
              c.success && c.data && digitsOnly(c.data.Id) === digitsOnly(eservCustomerQboId)
            );
            const isCustomerInactive = qboCustomerForMissing?.isActive === false;
            
            const showCustomerMissing = !!qboDataPayload && !qboLoading && (qboCustomerMissing || isCustomerInactive) && customerStatus?.type !== 'success';

            const remoteCustomerContent = (
              <div className="space-y-0.5">
                {it.customer_qbo_id ? (
                  showCustomerMissing ? (
                    <div className="rounded border border-red-200 bg-red-50 px-1 py-0.5 text-xs text-red-700">
                      Customer {it.customer_qbo_id} not found in QBO.
                    </div>
                  ) : (() => {
                    // Simple customer match logic: only use actual QBO customer data
                    const eservCustomerQboId = it.customer_qbo_id ? String(it.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
                    
                    // Find customer in QBO customers array only
                    const qboCustomer = qbo?.customers?.find((c: any) => 
                      c.success && c.data && String(c.data.Id) === String(eservCustomerQboId)
                    );
                    
                    // Match only if QBO customer exists and is active
                    const customerIdsMatch = eservCustomerQboId && qboCustomer && qboCustomer.data && 
                      qboCustomer.data.Active !== false;
                    
                    return (
                      <>
                        <div className="font-medium text-gray-900">{qboCustomerData?.DisplayName ?? '—'}</div>
                        <div className={`text-xs ${customerIdsMatch ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                          ID: {it.customer_qbo_id}
                        </div>
                        {qboCustomer?.error && (
                          <div className="text-xs text-orange-600">{String(qboCustomer.error)}</div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="text-xs text-gray-500">Not yet synced to QuickBooks.</div>
                )}
                {it.fk_bcustomer_id && (() => {
                  // Simple customer match logic: only use actual QBO customer data
                  const eservCustomerQboId = it.customer_qbo_id ? String(it.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
                  
                  // Find customer in QBO customers array only
                  const qboCustomer = qbo?.customers?.find((c: any) => 
                    c.success && c.data && String(c.data.Id) === String(eservCustomerQboId)
                  );
                  
                  // Match only if QBO customer exists and is active
                  const customerIdsMatch = eservCustomerQboId && qboCustomer && qboCustomer.data && 
                    qboCustomer.data.Active !== false;
                  
                  if (DEBUG_LOGGING) console.log('🔍 Customer sync button logic:', {
                    customerId: it.fk_bcustomer_id,
                    customerQboId: it.customer_qbo_id,
                    eservCustomerQboId,
                    showCustomerMissing,
                    hasQboCustomer: !!qboCustomer,
                    qboCustomerSuccess: qboCustomer?.success,
                    qboCustomerData: !!qboCustomer?.data,
                    qboCustomerId: qboCustomer?.data?.Id,
                    qboCustomerActive: qboCustomer?.isActive,
                    qboCustomerDataActive: qboCustomer?.data?.Active,
                    customerMatchState,
                    customerIdsMatch,
                    shouldShowButton: !customerIdsMatch,
                    qboCustomersCount: qbo?.customers?.length,
                    qboCustomers: qbo?.customers?.map(c => ({ id: c.data?.Id, success: c.success, active: c.isActive }))
                  });
                  
                  // Show button for inactive customers or no match
                  if (customerMatchState === 'match_active') return null;
                  
                  // Determine button styling based on state - match other sync buttons (orange style)
                  const getButtonStyling = () => {
                    if (customerMatchState === 'match_inactive') {
                      return 'px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-800 rounded border border-amber-300 hover:bg-amber-200 disabled:opacity-50';
                    }
                    return 'px-2 py-0.5 text-[11px] font-medium bg-orange-100 text-orange-800 rounded border border-orange-300 hover:bg-orange-200 disabled:opacity-50';
                  };
                  
                  return (
                    <>
                      <button
                        onClick={() => resyncCustomer(it.fk_bcustomer_id!)}
                        disabled={isCustomerSyncing}
                        className={getButtonStyling()}
                        title={customerMatchState === 'match_inactive' ? 'Resync as new customer (current is inactive)' : 'Sync customer to QuickBooks'}
                      >
                        {customerButtonLabel}
                      </button>
                      {customerStatus && (
                        <div className={`mt-0.5 rounded border px-1 py-0.5 text-[10px] ${customerStatusClass}`}>
                          {customerStatus.text}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            );

            return (
              <div key={it.payment_id} className={`rounded border ${isSelected ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'} shadow-sm`}>
                <div className="flex flex-wrap items-start justify-between gap-1 border-b border-gray-100 px-1 py-0.5.5">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Payment</div>
                    <div className="text-sm font-semibold text-gray-900">
                      EServ #{it.payment_id}
                      <span className="ml-1 text-xs font-normal text-gray-500">QBO {it.payment_qbo_id ?? '—'}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Received {formatDate(it.received_on)} · Created {formatDate(it.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    {qboLoading && isSelected && (
                      <span className="flex items-center gap-1 text-gray-400">
                        <span className="h-1.5 w-1.5 animate-spin rounded-full border-b-2 border-blue-500"></span>
                        Loading…
                      </span>
                    )}
                    <label className="flex items-center gap-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const on = (e.currentTarget as HTMLInputElement).checked;
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (on) next.add(it.payment_id); else next.delete(it.payment_id);
                            return next;
                          });
                        }}
                        className="h-3 w-3"
                      />
                      {isSelected ? 'Selected' : 'Select'}
                    </label>
                    {isSelected && it.payment_qbo_id && (
                      <button
                        type="button"
                        className="rounded bg-orange-500 px-1 py-0.5 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-40"
                        disabled={fixingIds.has(it.payment_id)}
                        onClick={async () => {
                          if (!it.payment_qbo_id) return;
                          setFixingIds(prev => new Set(prev).add(it.payment_id));
                          setBanner(null);
                          try {
                            const { correctAppliedPayments } = await import('services/reconciliationService');
                            const apps = (it.applications || [])
                              .filter(a => a.can_correct && typeof a.applied_amount === 'number')
                              .map(a => {
                                const invoiceIdValue = Number(a.invoice_id);
                                return {
                                  invoiceId: Number.isFinite(invoiceIdValue) ? invoiceIdValue : null,
                                  invoiceQboId: a.invoice_qbo_id ? String(a.invoice_qbo_id) : null,
                                  amountCents: Number(a.applied_amount)
                                };
                              });
                            const resp = await correctAppliedPayments([
                              { paymentQboId: String(it.payment_qbo_id), paymentId: it.payment_id, applications: apps }
                            ]);
                            const r = resp.results && resp.results[0];
                            const ok = !!(r && r.ok);

                            let bannerText = `Payment ${it.payment_id} corrected in QBO.`;
                            let bannerType: 'success' | 'error' | 'info' = ok ? 'success' : 'error';

                            if (r && r.details) {
                              const { updateSuccessful, actualUnapplied, expectedUnapplied, missingInvoices, syncResults, taxErrors } = r.details;

                              if (updateSuccessful === false) {
                                bannerText = `Payment ${it.payment_id}: QBO rejected the correction (expected unapplied ${expectedUnapplied}, actual ${actualUnapplied}).`;
                                bannerType = 'error';
                              }
                              if (missingInvoices && missingInvoices.length > 0) {
                                bannerText += ` Missing invoices: ${missingInvoices.join(', ')}.`;
                                bannerType = 'error';
                              }
                              if (taxErrors && taxErrors.length > 0) {
                                bannerText += ` Tax issues on invoices: ${taxErrors.join(', ')}.`;
                                bannerType = 'error';
                              }
                              if (syncResults && syncResults.length > 0) {
                                const successfulSyncs = syncResults.filter(s => s.success).length;
                                const failedSyncs = syncResults.filter(s => !s.success).length;
                                if (successfulSyncs > 0) {
                                  bannerText += ` Synced ${successfulSyncs} invoice(s).`;
                                }
                                if (failedSyncs > 0) {
                                  bannerText += ` Failed to sync ${failedSyncs} invoice(s).`;
                                  bannerType = 'error';
                                }
                              }
                            }

                            setBanner({ type: bannerType, text: bannerText });
                            if (!ok) {
                              console.warn('Correction details:', {
                                paymentId: it.payment_id,
                                paymentQboId: it.payment_qbo_id,
                                details: r?.details
                              });
                            }
                          } catch (error: any) {
                            console.error('Correction error:', error);
                            setBanner({ type: 'error', text: error?.message || 'Correction failed' });
                          } finally {
                            setFixingIds(prev => {
                              const next = new Set(prev);
                              next.delete(it.payment_id);
                              return next;
                            });
                          }
                        }}
                      >
                        {fixingIds.has(it.payment_id) ? 'Correcting…' : 'Auto-correct'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Show comparison table only for selected payments with QBO data */}
                {isSelected && qbo && (
                  <div className="overflow-x-auto px-2">
                    <table className="min-w-full border-separate border-spacing-y-0 text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                          <th className="w-24 rounded-l px-1 py-1 text-left">Section</th>
                          <th className="px-1 py-1 text-left">EServ</th>
                          <th className="rounded-r px-1 py-1 text-left">QuickBooks Online</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700">
                        {paymentMetrics.map((metric) => (
                          <tr key={metric.label} className="align-top">
                            <th className="whitespace-nowrap px-1 py-0.5 text-left font-medium text-gray-600">{metric.label}</th>
                            <td className="px-1 py-0.5">{metric.local}</td>
                            <td className="px-1 py-0.5">{metric.remote}</td>
                          </tr>
                        ))}
                        <tr className="align-top">
                          <th className="whitespace-nowrap px-1 py-0.5 text-left font-medium text-gray-600">Customer</th>
                          <td className="px-1 py-0.5">{localCustomerContent}</td>
                          <td className="px-1 py-0.5">{remoteCustomerContent}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Show invoice details only for selected payments with QBO data */}
                {isSelected && qbo && (
                  <div className="mt-1 overflow-x-auto px-2 pb-2">
                    <table className="min-w-full border-collapse text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                          <th className="rounded-l px-1 py-1 text-left">Invoice</th>
                          <th className="px-1 py-1 text-left">EServ Applied</th>
                          <th className="px-1 py-1 text-left">QBO Applied</th>
                          <th className="px-1 py-1 text-left">Status</th>
                          <th className="rounded-r px-1 py-1 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-gray-700">
                        {(it.applications || []).map((a, idx) => {
                          const applyKey = `${it.payment_id}-${a.invoice_id}`;
                          const isApplying = applyingPayments.has(applyKey);
                          const isSyncing = syncingInvoices.has(a.invoice_id);

                          const inv = a.invoice_qbo_id ? invoiceMap.get(String(a.invoice_qbo_id)) : null;
                          const ok = inv && inv.success;
                          const invoiceAppliedAmount = qboPayment && qboPayment.Line
                            ? qboPayment.Line.reduce((sum, line) => {
                                if (line.LinkedTxn && line.LinkedTxn.some(link => link.TxnId === a.invoice_qbo_id)) {
                                  return sum + (Number(line.Amount) || 0);
                                }
                                return sum;
                              }, 0)
                            : 0;
                          const actualCents = Math.round(invoiceAppliedAmount * 100);
                          const expectedCents = Number(a.applied_amount || 0);
                          const isMismatch = qboPayment ? actualCents !== expectedCents : false;
                          const isMissing = !a.can_correct;
                          const isMissingInQbo = a.invoice_qbo_id && !ok;

                          let statusLabel = 'Ready';
                          let statusClasses = 'rounded border border-green-200 bg-green-50 px-1 py-0.5 text-[10px] font-medium text-green-700';

                          if (isMissing) {
                            statusLabel = 'Not synced to QBO';
                            statusClasses = 'rounded border border-blue-200 bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-700';
                          } else if (isMissingInQbo) {
                            statusLabel = 'Missing in QBO';
                            statusClasses = 'rounded border border-red-200 bg-red-50 px-1 py-0.5 text-[10px] font-medium text-red-700';
                          } else if (isMismatch) {
                            statusLabel = 'Mismatch';
                            statusClasses = 'rounded border border-red-200 bg-red-50 px-1 py-0.5 text-[10px] font-medium text-red-700';
                          }

                          return (
                            <tr key={idx} className="align-top">
                              <td className="px-1 py-1">
                                <div className="font-medium text-gray-900">#{a.invoice_id}</div>
                                <div className="text-[10px] text-gray-500">QBO {a.invoice_qbo_id ?? '—'}</div>
                              </td>
                              <td className="px-1 py-1">{formatMoney(a.applied_amount as any)}</td>
                              <td className="px-1 py-1">{qboPayment ? formatMoney(actualCents) : '$0.00'}</td>
                              <td className="px-1 py-1">
                                <span className={statusClasses}>{statusLabel}</span>
                                {!ok && a.invoice_qbo_id && (
                                  <div className="mt-0.5 text-[9px] text-red-600">Unable to fetch invoice from QBO.</div>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <div className="flex flex-wrap gap-1">
                                  {isMissing && (
                                    <button
                                      onClick={() => syncInvoice(a.invoice_id)}
                                      disabled={isSyncing}
                                      className="rounded border border-blue-200 bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {isSyncing ? 'Syncing…' : 'Sync to QBO'}
                                    </button>
                                  )}
                                  {!isMissing && it.payment_qbo_id && a.invoice_qbo_id && (
                                    <button
                                      onClick={() => applyPayment(it.payment_id, a.invoice_id, a.invoice_qbo_id!, it.payment_qbo_id!, a.applied_amount)}
                                      disabled={isApplying}
                                      className="rounded bg-green-100 text-green-700 px-1 py-0.5 text-[10px] font-medium hover:bg-green-200 disabled:cursor-not-allowed disabled:opacity-50 border border-green-200"
                                    >
                                      {isApplying ? 'Applying…' : 'Apply in QBO'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
            <div>
              Page {page} of {Math.max(Math.ceil(total / pageSize), 1)} · {total} total payments
              {getFilteredItems(items).length !== items.length && (
                <span className="ml-2 text-orange-600">
                  · {getFilteredItems(items).length} filtered
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button className="px-1 py-0.5 rounded border border-gray-300 hover:bg-gray-50 text-xs" disabled={page <= 1 || loading} onClick={() => loadPage(page - 1, pageSize, searchTerm)}>Prev</button>
              <button className="px-1 py-0.5 rounded border border-gray-300 hover:bg-gray-50 text-xs" disabled={page >= Math.max(Math.ceil(total / pageSize), 1) || loading} onClick={() => loadPage(page + 1, pageSize, searchTerm)}>Next</button>
            </div>
          </div>
        </div>
      </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 p-2 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {loading && <span className="animate-pulse">Working…</span>}
              {error && <span className="text-red-600">{error}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className="px-1 py-0.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={refreshData} disabled={loading}>Refresh Data</button>
            </div>
          </div>
        </>
      )}

      {/* Dev Tab Content */}
      {activeTab === 'dev' && (
        <DevTab
          qboConnectionStatus={qboConnectionStatus}
          qboTokenInfo={qboTokenInfo}
          qboConnectionData={connectionStatus}
          items={items}
        />
      )}

      {/* Legacy Dev Tab Content - Remove this section */}
      {false && activeTab === 'dev' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-4">
            <div className="max-w-7xl mx-auto">
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium w-32">Step</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium w-48">Action</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium w-64">EServ</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium w-64">QBO</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium w-24">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Token Connection */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-medium">1. Token Connection</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="text-sm">Check QBO API connection</div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="font-mono text-xs text-gray-500">N/A</div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="font-mono text-xs">
                        Status: {qboConnectionStatus}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        qboConnectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
                        qboConnectionStatus === 'disconnected' ? 'bg-red-100 text-red-800' :
                        qboConnectionStatus === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {qboConnectionStatus === 'connected' ? '✅ Connected' :
                         qboConnectionStatus === 'disconnected' ? '❌ Disconnected' :
                         qboConnectionStatus === 'error' ? '❌ Error' :
                         '⏳ Loading'}
                      </span>
                    </td>
                  </tr>

                  {/* Get Counts Button */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-medium">2. Get QBO Counts</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <button
                        onClick={async () => {
                          try {
                            setLoading(true);
                            
                            // Fetch both QBO and EServ totals in parallel
                            const [qboResponse, eservResponse] = await Promise.all([
                              fetchQboTotals(),
                              axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/local-summary`)
                            ]);
                            
                            console.log('QBO Totals Response:', qboResponse);
                            console.log('EServ Totals Response:', eservResponse.data);
                            
                            setQboTotals(qboResponse);
                            setEservTotals(eservResponse.data);
                          } catch (error) {
                            console.error('Failed to fetch totals:', error);
                            setQboTotals({ error: error.message });
                            setEservTotals({ error: error.message });
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Get Counts'}
                      </button>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      {eservTotals && (
                        <div className="font-mono text-xs">
                          <div className="bg-gray-50 p-2 rounded border">
                            <div>Invoices: {eservTotals.totalInvoices || 0}</div>
                            <div>Paid: {eservTotals.paidInvoices || 0}</div>
                            <div>Unpaid: {eservTotals.unpaidInvoices || 0}</div>
                            <div>Payments: {eservTotals.totalPayments || 0}</div>
                            <div>Amount: ${eservTotals.totalAmount || 0}</div>
                            <div>Customers: {eservTotals.totalCustomers || 0}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      {qboTotals && (
                        <div className="font-mono text-xs">
                          <div className="bg-gray-50 p-2 rounded border">
                            <div>Invoices: {qboTotals.totalInvoices || 0}</div>
                            <div>Paid: {qboTotals.paidInvoices || 0}</div>
                            <div>Unpaid: {qboTotals.unpaidInvoices || 0}</div>
                            <div>Payments: {qboTotals.totalPayments || 0}</div>
                            <div>Amount: ${qboTotals.totalAmount || 0}</div>
                            <div>Customers: {qboTotals.totalCustomers || 0}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        loading ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {loading ? '⏳ Loading' : '⏸️ Ready'}
                      </span>
                    </td>
                  </tr>

                  {/* Customer Lookup */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-medium">3. Customer Lookup</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Enter Customer ID"
                          value={customerSearchId}
                          onChange={(e) => setCustomerSearchId(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded text-sm w-32"
                        />
                        <button
                          onClick={async () => {
                            try {
                              setLoading(true);
                              
                              if (customerSearchId.trim()) {
                                // Find specific customer by ID using reconciliation API
                                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/customers/${customerSearchId.trim()}`);
                                setSearchedCustomer(response.data);
                                console.log('Found customer:', response.data);
                              } else {
                                // Get first customer by ID descending using reconciliation API
                                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/customers`, {
                                  params: { 
                                    page: 1, 
                                    pageSize: 1,
                                    search: ''
                                  }
                                });
                                if (response.data?.customers && response.data.customers.length > 0) {
                                  const firstCustomer = response.data.customers[0];
                                  setSearchedCustomer(firstCustomer);
                                  console.log('First customer (ID descending):', firstCustomer);
                                } else {
                                  setSearchedCustomer({ error: 'No customers found' });
                                }
                              }
                            } catch (error) {
                              console.error('Failed to lookup customer:', error);
                              setSearchedCustomer({ error: error.message });
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                          disabled={loading}
                        >
                          {loading ? '...' : 'Lookup'}
                        </button>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      {searchedCustomer && (
                        <div className="font-mono text-xs">
                          <div className="bg-gray-50 p-2 rounded border">
                            <div>ID: {searchedCustomer.id || 'N/A'}</div>
                            <div>Name: {searchedCustomer.first_name} {searchedCustomer.last_name}</div>
                            <div>Company: {searchedCustomer.company_name || 'N/A'}</div>
                            <div>QBO ID: {searchedCustomer.customer_qbo_id || 'N/A'}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      <div className="font-mono text-xs text-gray-500">N/A</div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        searchedCustomer ? 
                          (searchedCustomer.error ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800') :
                          'bg-gray-100 text-gray-800'
                      }`}>
                        {searchedCustomer ? 
                          (searchedCustomer.error ? '❌ Error' : '✅ Found') :
                          '⏸️ Ready'}
                      </span>
                    </td>
                  </tr>

                  {/* Invoice Lookup */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-medium">4. Invoice Lookup</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Enter Invoice ID"
                          value={invoiceSearchId}
                          onChange={(e) => setInvoiceSearchId(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded text-sm w-32"
                        />
                        <button
                          onClick={async () => {
                            try {
                              setLoading(true);
                              
                              if (invoiceSearchId.trim()) {
                                // Find specific invoice by ID
                                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${invoiceSearchId.trim()}`);
                                setSearchedInvoice(response.data);
                                console.log('Found invoice:', response.data);
                              } else {
                                // Get first invoice by ID descending
                                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoices`, {
                                  params: { page: 1, pageSize: 1, column: 'id', order: 'desc' }
                                });
                                if (response.data?.invoices && response.data.invoices.length > 0) {
                                  const firstInvoice = response.data.invoices[0];
                                  setSearchedInvoice(firstInvoice);
                                  console.log('First invoice (ID descending):', firstInvoice);
                                } else {
                                  setSearchedInvoice({ error: 'No invoices found' });
                                }
                              }
                            } catch (error) {
                              console.error('Failed to lookup invoice:', error);
                              setSearchedInvoice({ error: error.message });
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                          disabled={loading}
                        >
                          {loading ? '...' : 'Lookup'}
                        </button>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      {searchedInvoice && (
                        <div className="font-mono text-xs">
                          <div className="bg-gray-50 p-2 rounded border">
                            <div>ID: {searchedInvoice.id || 'N/A'}</div>
                            <div>Amount: ${searchedInvoice.amount_due || 0}</div>
                            <div>Status: {searchedInvoice.status?.label || 'N/A'}</div>
                            <div>QBO ID: {searchedInvoice.invoice_qbo_id || 'N/A'}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      <div className="font-mono text-xs text-gray-500">N/A</div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        searchedInvoice ? 
                          (searchedInvoice.error ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800') :
                          'bg-gray-100 text-gray-800'
                      }`}>
                        {searchedInvoice ? 
                          (searchedInvoice.error ? '❌ Error' : '✅ Found') :
                          '⏸️ Ready'}
                      </span>
                    </td>
                  </tr>

                  {/* Payment Lookup */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-medium">5. Payment Lookup</td>
                    <td className="border border-gray-300 px-3 py-2">
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Enter Payment ID"
                          value={paymentSearchId}
                          onChange={(e) => setPaymentSearchId(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded text-sm w-32"
                        />
                        <button
                          onClick={async () => {
                            try {
                              setLoading(true);
                              
                              if (paymentSearchId.trim()) {
                                // Find specific payment in current items
                                const payment = items.find(item => item.payment_id.toString() === paymentSearchId.trim());
                                if (payment) {
                                  setSearchedPayment(payment);
                                  console.log('Found payment:', payment);
                                } else {
                                  setSearchedPayment({ error: 'Payment not found in current items' });
                                }
                              } else {
                                // Get first payment by ID descending
                                const response = await getAppliedPaymentCandidates(1, 1, '');
                                if (response?.items && response.items.length > 0) {
                                  const firstPayment = response.items[0];
                                  setSearchedPayment(firstPayment);
                                  console.log('First payment (ID descending):', firstPayment);
                                } else {
                                  setSearchedPayment({ error: 'No payments found' });
                                }
                              }
                            } catch (error) {
                              console.error('Failed to lookup payment:', error);
                              setSearchedPayment({ error: error.message });
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                          disabled={loading}
                        >
                          {loading ? '...' : 'Lookup'}
                        </button>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      {searchedPayment && (
                        <div className="font-mono text-xs">
                          <div className="bg-gray-50 p-2 rounded border">
                            <div>ID: {searchedPayment.payment_id || 'N/A'}</div>
                            <div>Amount: ${searchedPayment.payment_amount || 0}</div>
                            <div>Date: {searchedPayment.received_on || 'N/A'}</div>
                            <div>QBO ID: {searchedPayment.payment_qbo_id || 'N/A'}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 max-w-64">
                      <div className="font-mono text-xs text-gray-500">N/A</div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        searchedPayment ? 
                          (searchedPayment.error ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800') :
                          'bg-gray-100 text-gray-800'
                      }`}>
                        {searchedPayment ? 
                          (searchedPayment.error ? '❌ Error' : '✅ Found') :
                          '⏸️ Ready'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Crawl Tab Content */}
      {activeTab === 'crawl' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Crawl Header */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Reconciliation Crawler</h2>
                <p className="text-sm text-gray-600">Methodical one-by-one reconciliation to avoid rate limits</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Payment search input - always visible */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Jump to Payment ID:</label>
                  <input
                    type="number"
                    value={startPaymentId}
                    onChange={(e) => setStartPaymentId(e.target.value)}
                    placeholder="e.g., 44501"
                    className="w-32 px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={async () => {
                      if (startPaymentId) {
                        if (DEBUG_LOGGING) console.log('🚀 JUMP BUTTON CLICKED - Payment ID:', startPaymentId);
                        // Reset crawl state
                        setCurrentPage(1);
                        setCrawlStatus('ready');
                        setCrawlUiState('idle');
                        setCurrentPayment(null);
                        setCrawlProgress(null);
                        clearCrawlAutoAdvance();
                        setCrawlAdvanceCountdown(null);
                        
                        // Load the specific payment directly
                        try {
                          setCrawlUiState('loading');
                          const response = await getAppliedPaymentCandidates(1, 1, startPaymentId);
                          
                          if (response.items && response.items.length > 0) {
                            const targetPayment = response.items[0];
                            setCurrentPayment(targetPayment);
                            setCurrentPage(1); // Reset page when jumping
                            setCrawlProgress({
                              current: 1,
                              total: response.total,
                              processed: 0,
                              reconciled: 0,
                              needsFix: 0
                            });
                            
                            // Fetch QBO data for this payment
                            if (DEBUG_LOGGING) console.log('🔍 Jump: Fetching QBO data for payment', targetPayment.payment_id);
                            const qboData = await fetchQboDataForItems([targetPayment]);
                            if (DEBUG_LOGGING) console.log('🔍 Jump: QBO data fetched', !!qboData);
                            
                            if (targetPayment.payment_qbo_id && !qboData) {
                              setBanner({ type: 'error', text: `QuickBooks data unavailable for payment ${targetPayment.payment_id}.` });
                            }
                            
                            // Set crawl status to crawling
                            setCrawlStatus('crawling');
                            
                            // Evaluate the payment to check if it matches
                            if (DEBUG_LOGGING) console.log('🔍 Jump: About to call evaluateCrawlPayment');
                            evaluateCrawlPayment(targetPayment, qboData);
                            if (DEBUG_LOGGING) console.log('🔍 Jump: evaluateCrawlPayment called');
                            
                            setBanner({ type: 'info', text: `Loaded payment ${targetPayment.payment_id}. Ready to review.` });
                          } else {
                            setBanner({ type: 'error', text: `No payment found with ID ${startPaymentId}` });
                            setCrawlUiState('idle');
                          }
                        } catch (error: any) {
                          console.error('Failed to load payment:', error);
                          setBanner({ type: 'error', text: `Failed to load payment: ${error?.message || 'Unknown error'}` });
                          setCrawlUiState('idle');
                        }
                      }
                    }}
                    disabled={!startPaymentId}
                    className="px-3 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-md hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Jump
                  </button>
                </div>

                {crawlStatus === 'ready' && (
                  <button
                    onClick={startCrawl}
                    className="px-2 py-1 bg-green-100 text-green-700 border border-green-200 text-sm font-medium rounded-md hover:bg-green-200"
                  >
                    Start Crawl
                  </button>
                )}
                {/* Stop on Mismatch Toggle - Always visible */}
                <div className="flex items-center gap-1 px-1 py-0.5 bg-gray-50 rounded border text-xs">
                  <span className="text-gray-500">Stop on Mismatch:</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setStopOnMismatch(!stopOnMismatch);
                    }}
                    className={`relative inline-flex h-3 w-6 items-center rounded-full transition-colors ${
                      stopOnMismatch ? 'bg-blue-400' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-2 w-2 transform rounded-full bg-white transition-transform ${
                        stopOnMismatch ? 'translate-x-3' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="text-gray-400 text-xs">
                    {stopOnMismatch ? 'ON' : 'OFF'}
                  </span>
                </div>

                {crawlStatus === 'crawling' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={isAutoAdvancePaused ? toggleAutoAdvance : pauseCrawl}
                      className={`px-2 py-1 text-sm font-medium rounded-md border ${
                        isAutoAdvancePaused 
                          ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' 
                          : 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200'
                      }`}
                    >
                      {isAutoAdvancePaused ? (
                        <>
                          <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                          Resume
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1zM12 7a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Pause
                        </>
                      )}
                    </button>
                    <button
                      onClick={resetCrawl}
                      className="px-2 py-1 bg-gray-100 text-gray-700 border border-gray-200 text-sm font-medium rounded-md hover:bg-gray-200"
                    >
                      Reset
                    </button>
                    {auditLog.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowAuditLog(!showAuditLog)}
                          className="px-2 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200 text-sm font-medium rounded-md hover:bg-indigo-200"
                        >
                          {showAuditLog ? 'Hide' : 'View'} Log ({auditLog.length})
                        </button>
                        <button
                          onClick={downloadAuditLog}
                          className="px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-md hover:bg-purple-200"
                        >
                          Download
                        </button>
                      </>
                    )}
                    {qboErrors.length > 0 && (
                      <button
                        onClick={() => setShowQboErrors(!showQboErrors)}
                        className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 text-sm font-medium rounded-md hover:bg-red-200"
                      >
                        QBO Errors ({qboErrors.length})
                      </button>
                    )}
                  </div>
                )}
                {crawlStatus === 'paused' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={resumeCrawl}
                      className="px-2 py-1 bg-green-100 text-green-700 border border-green-200 text-sm font-medium rounded-md hover:bg-green-200"
                    >
                      Continue
                    </button>
                    <button
                      onClick={resetCrawl}
                      className="px-2 py-1 bg-gray-100 text-gray-700 border border-gray-200 text-sm font-medium rounded-md hover:bg-gray-200"
                    >
                      Reset
                    </button>
                    {auditLog.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowAuditLog(!showAuditLog)}
                          className="px-2 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200 text-sm font-medium rounded-md hover:bg-indigo-200"
                        >
                          {showAuditLog ? 'Hide' : 'View'} Log ({auditLog.length})
                        </button>
                        <button
                          onClick={downloadAuditLog}
                          className="px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-md hover:bg-purple-200"
                        >
                          Download
                        </button>
                      </>
                    )}
                    {qboErrors.length > 0 && (
                      <button
                        onClick={() => setShowQboErrors(!showQboErrors)}
                        className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 text-sm font-medium rounded-md hover:bg-red-200"
                      >
                        QBO Errors ({qboErrors.length})
                      </button>
                    )}
                  </div>
                )}
                {crawlStatus === 'completed' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={resetCrawl}
                      className="px-2 py-1 bg-blue-100 text-blue-700 border border-blue-200 text-sm font-medium rounded-md hover:bg-blue-200"
                    >
                      Start New Crawl
                    </button>
                    {auditLog.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowAuditLog(!showAuditLog)}
                          className="px-2 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200 text-sm font-medium rounded-md hover:bg-indigo-200"
                        >
                          {showAuditLog ? 'Hide' : 'View'} Log ({auditLog.length})
                        </button>
                        <button
                          onClick={downloadAuditLog}
                          className="px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-md hover:bg-purple-200"
                        >
                          Download
                        </button>
                      </>
                    )}
                    {qboErrors.length > 0 && (
                      <button
                        onClick={() => setShowQboErrors(!showQboErrors)}
                        className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 text-sm font-medium rounded-md hover:bg-red-200"
                      >
                        QBO Errors ({qboErrors.length})
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Crawl Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Banner Messages */}
            {banner && (
              <div className={`mb-4 ${banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : banner.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'} border rounded-md p-3 text-sm`}>
                {banner.text}
              </div>
            )}


            {/* Audit Log Display */}
            {showAuditLog && auditLog.length > 0 && (
              <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Audit Log</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{auditLog.length} entries</span>
                    <button
                      onClick={downloadAuditLog}
                      className="px-3 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-xs font-medium rounded hover:bg-purple-200"
                    >
                      Download
                    </button>
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-3">
                    {auditLog.map((entry, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-1 py-0.5 rounded-full text-xs font-medium ${
                              entry.action === 'processed' ? 'bg-green-100 text-green-800' :
                              entry.action === 'needs_fix' ? 'bg-red-100 text-red-800' :
                              entry.action === 'skipped' ? 'bg-yellow-100 text-yellow-800' :
                              entry.action === 'started' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {entry.action.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="font-medium">Payment #{entry.paymentId}</span>
                          </div>
                          <span className="text-gray-500 text-xs">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="font-medium text-gray-700 mb-1">EServ Data:</div>
                            <div className="space-y-1 text-gray-600">
                              <div>Amount: {formatMoney(entry.details.eservData.amount)}</div>
                              <div>Customer: {entry.details.eservData.customer}</div>
                              <div>Customer ID: {entry.details.eservData.customerId || 'N/A'}</div>
                              <div>Applications: {entry.details.eservData.applications.length}</div>
                            </div>
                          </div>
                          <div>
                            <div className="font-medium text-gray-700 mb-1">QBO Data:</div>
                            <div className="space-y-1 text-gray-600">
                              <div>QBO ID: {entry.details.qboData.paymentQboId || 'Not synced'}</div>
                              <div>Customer QBO ID: {entry.details.qboData.customerQboId || 'N/A'}</div>
                              <div>Synced: {entry.details.qboData.synced ? 'Yes' : 'No'}</div>
                            </div>
                          </div>
                        </div>
                        
                        {entry.details.notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-gray-700">
                            <strong>Notes:</strong> {entry.details.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* QBO Error Display */}
            {showQboErrors && qboErrors.length > 0 && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-red-900">QBO API Errors</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600">{qboErrors.length} errors</span>
                    <button
                      onClick={() => setQboErrors([])}
                      className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 text-xs font-medium rounded hover:bg-red-200"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-3">
                    {qboErrors.map((error, index) => (
                      <div key={index} className="border border-red-200 rounded-lg p-3 text-sm bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="px-1 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {error.operation}
                            </span>
                          </div>
                          <span className="text-gray-500 text-xs">
                            {new Date(error.timestamp).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="text-red-700 mb-2">
                          <strong>Error:</strong> {error.error}
                        </div>
                        
                        {error.details && (
                          <details className="text-xs text-gray-600">
                            <summary className="cursor-pointer text-red-600 hover:text-red-800">
                              Show Details
                            </summary>
                            <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                              {JSON.stringify(error.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Totals Comparison - Hidden during crawl */}
            {crawlTotals && crawlStatus !== 'crawling' && (
              <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Data Comparison</h3>
                  <button
                    onClick={() => setIsDataComparisonCollapsed(!isDataComparisonCollapsed)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {isDataComparisonCollapsed ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        Show Details
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Hide Details
                      </>
                    )}
                  </button>
                </div>
                {!isDataComparisonCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* EServ Data */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <h4 className="font-medium text-gray-900">EServ Local Data</h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Customers:</span>
                        <span className="font-medium">{crawlTotals.eserv.totalCustomers.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Invoices:</span>
                        <span className="font-medium">{crawlTotals.eserv.totalInvoices.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Payments:</span>
                        <span className="font-medium">{crawlTotals.eserv.totalPayments.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Paid Invoices:</span>
                        <span className="font-medium text-green-600">{crawlTotals.eserv.paidInvoices.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Unpaid Invoices:</span>
                        <span className="font-medium text-red-600">{crawlTotals.eserv.unpaidInvoices.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-medium">{formatMoney(crawlTotals.eserv.totalAmount)}</span>
                      </div>
                    </div>
                  </div>

                  {/* QBO Data */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <h4 className="font-medium text-gray-900">QuickBooks Online</h4>
                      </div>
                      <ConnectionIndicator status={qboConnectionStatus} />
                    </div>
                    {crawlTotals.qbo ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Customers:</span>
                          <span className="font-medium">{crawlTotals.qbo.totalCustomers.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Invoices:</span>
                          <span className="font-medium">{crawlTotals.qbo.totalInvoices.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Payments:</span>
                          <span className="font-medium">{crawlTotals.qbo.totalPayments.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Paid Invoices:</span>
                          <span className="font-medium text-green-600">{crawlTotals.qbo.paidInvoices.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unpaid Invoices:</span>
                          <span className="font-medium text-red-600">{crawlTotals.qbo.unpaidInvoices.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-medium">{formatMoney(crawlTotals.qbo.totalAmount)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        QBO data not available. Click "Fetch QBO Data" to load.
                      </div>
                    )}
                  </div>
                </div>
                )}
                {isDataComparisonCollapsed && (
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <span>EServ: {crawlTotals.eserv.totalCustomers} customers, {crawlTotals.eserv.totalInvoices} invoices, {crawlTotals.eserv.totalPayments} payments</span>
                      <span>QBO: {crawlTotals.qbo?.totalCustomers || 0} customers, {crawlTotals.qbo?.totalInvoices || 0} invoices, {crawlTotals.qbo?.totalPayments || 0} payments</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Crawl Progress - Compact */}
            {crawlProgress && (
              <div className="mb-3 bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-gray-900">
                      {auditLog.filter(entry => entry.action === 'processed' || entry.action === 'needs_fix').length} of {crawlProgress.total} payments
                    </span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-green-600">✓ {auditLog.filter(entry => entry.action === 'processed').length}</span>
                      <span className="text-red-600">⚠ {auditLog.filter(entry => entry.action === 'needs_fix').length}</span>
                      <span className="text-blue-600">→ {crawlProgress.current}</span>
                    </div>
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${(auditLog.filter(entry => entry.action === 'processed' || entry.action === 'needs_fix').length / crawlProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Current Payment Details */}
            {currentPayment && (
              <div className="relative bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Current Payment Comparison</h3>
                
                {/* Auto-advance countdown - moved to top */}
                {crawlStatus === 'crawling' && crawlUiState === 'matched' && crawlAdvanceCountdown !== null && crawlProgress && crawlProgress.current < crawlProgress.total && (
                  <div className="mb-6 flex justify-center">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-center">
                      <div className="text-blue-800 font-medium">
                        ✅ Payment data matches! Auto-advancing in {crawlAdvanceCountdown} seconds...
                      </div>
                      <div className="text-blue-600 text-sm mt-1">
                        Click "Mark as Processed" or "Mark as Needs Fix" to override
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* EServ Payment Data */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <h4 className="font-medium text-gray-900">EServ Payment</h4>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment ID:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            #{currentPayment.payment_id}
                            {currentPayment.payment_qbo_id && ` / QBO: ${currentPayment.payment_qbo_id}`}
                          </span>
                          {!currentPayment.payment_qbo_id && (
                            <button
                              onClick={() => syncPayment(currentPayment.payment_id)}
                              disabled={syncingItems.has(`payment-${currentPayment.payment_id}`)}
                              className="px-1 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                            >
                              <IoCard className="w-3 h-3" />
                              {syncingItems.has(`payment-${currentPayment.payment_id}`) ? 'Syncing...' : 'Sync'}
                            </button>
                          )}
                          {currentPayment.payment_qbo_id && (() => {
                            const paymentMap = new Map<string, any>();
                            (qboPayload?.payments || []).forEach((pay: any) => paymentMap.set(String(pay.id), pay));
                            const qboPayment = paymentMap.get(String(currentPayment.payment_qbo_id));
                            const qboPaymentMissing = currentPayment.payment_qbo_id && (!qboPayment || !qboPayment.success || !qboPayment.data);
                            return qboPaymentMissing;
                          })() && (
                            <button
                              onClick={() => syncPayment(currentPayment.payment_id)}
                              disabled={syncingItems.has(`payment-${currentPayment.payment_id}`)}
                              className="px-1 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-xs rounded hover:bg-orange-200 disabled:opacity-50 flex items-center gap-1"
                            >
                              <IoRefresh className="w-3 h-3" />
                              {syncingItems.has(`payment-${currentPayment.payment_id}`) ? 'Syncing...' : 'Sync'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">{formatMoney(currentPayment.payment_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer:</span>
                        <span className="font-medium">{currentPayment.company_name || `${currentPayment.first_name} ${currentPayment.last_name}`}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer ID:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {currentPayment.fk_bcustomer_id || 'N/A'}
                            {currentPayment.customer_qbo_id && ` / QBO: ${currentPayment.customer_qbo_id}`}
                          </span>
                          {!currentPayment.customer_qbo_id && currentPayment.fk_bcustomer_id && (
                            <button
                              onClick={() => syncCustomer(currentPayment.fk_bcustomer_id!)}
                              disabled={syncingItems.has(`customer-${currentPayment.fk_bcustomer_id}`)}
                              className="px-1 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                            >
                              <IoPersonAdd className="w-3 h-3" />
                              {syncingItems.has(`customer-${currentPayment.fk_bcustomer_id}`) ? 'Syncing...' : 'Sync'}
                            </button>
                          )}
                          {currentPayment.customer_qbo_id && currentPayment.fk_bcustomer_id && (() => {
                            // Simple customer match logic: only use actual QBO customer data
                            const eservCustomerQboId = currentPayment.customer_qbo_id ? String(currentPayment.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
                            
                            // Find customer in QBO customers array only
                            const qboCustomerData = qbo?.customers?.find((c: any) => 
                              c.success && c.data && String(c.data.Id) === String(eservCustomerQboId)
                            );
                            
                            // Match only if QBO customer exists and is active
                            const customerIdsMatch = eservCustomerQboId && qboCustomerData && qboCustomerData.data && 
                              qboCustomerData.data.Active !== false;
                            
                            // Hide sync button if customer IDs match and customer is active
                            return !customerIdsMatch;
                          })() && (() => {
                            // Determine button text and styling for inactive customers
                            const eservCustomerQboId = currentPayment.customer_qbo_id ? String(currentPayment.customer_qbo_id).replace(/.*\/\s*QBO:\s*/, '') : null;
                            const qboCustomerData = qbo?.customers?.find((c: any) => 
                              c.success && c.data && digitsOnly(c.data.Id) === digitsOnly(eservCustomerQboId)
                            );
                            
                            // Debug logging
                            console.log('Customer resolution debug:', {
                              eservCustomerQboId,
                              qboCustomerData,
                              isActive: qboCustomerData?.isActive,
                              active: qboCustomerData?.data?.Active,
                              syncToken: qboCustomerData?.data?.SyncToken,
                              fullData: qboCustomerData?.data
                            });
                            
                            // Check for inactive customers (including deleted ones marked as inactive)
                            const isInactive = qboCustomerData?.isActive === false || qboCustomerData?.data?.Active === false;
                            const isSyncing = syncingItems.has(`customer-${currentPayment.fk_bcustomer_id}`);
                            
                            return (
                            <div className="flex gap-1">
                              {isInactive && (
                                <button
                                  onClick={() => activateCustomer(currentPayment.fk_bcustomer_id!)}
                                  disabled={isSyncing}
                                  className="px-1 py-0.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                                  title="Activate existing customer in QBO"
                                >
                                  <IoCheckmark className="w-3 h-3" />
                                  Activate
                                </button>
                              )}
                              <button
                                onClick={() => syncCustomer(currentPayment.fk_bcustomer_id!)}
                                disabled={isSyncing}
                                className={`px-2 py-1 text-xs rounded disabled:opacity-50 flex items-center gap-1 ${
                                  isInactive 
                                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200' 
                                    : 'bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-200'
                                }`}
                                title={isInactive ? 'Resync as new customer (current is inactive)' : 'Sync customer to QuickBooks'}
                              >
                                <IoRefresh className="w-3 h-3" />
                                {isSyncing ? 'Syncing...' : (isInactive ? 'Resync as New' : 'Sync')}
                              </button>
                            </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Date:</span>
                        <span className="font-medium">{currentPayment.received_on ? new Date(currentPayment.received_on).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>

                    {/* Invoice Applications */}
                    <div className="mt-4">
                      <h5 className="font-medium text-gray-900 mb-2">Applied to Invoices:</h5>
                      <div className="space-y-2">
                        {currentPaymentApplications.map(({ app, invoiceQboId, expectedAppliedCents, qboAppliedCents, shouldShowResync, shouldShowApplyButton, applyDisabledReason, invoiceAppliedCorrectly, invoiceStatusClass }, index) => {
                          const hideResync = app.invoice_id != null && recentlySyncedInvoices.has(app.invoice_id);
                          return (
                          <div key={`${app.invoice_id}-${invoiceQboId ?? 'local'}-${index}`} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                            <div className="flex flex-col">
                              <span className="text-gray-600">
                                Invoice #{app.invoice_id}
                                {invoiceQboId && ` / QBO: ${invoiceQboId}`}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                EServ Applied: {formatMoney(app.applied_amount)}
                              </span>
                              {invoiceAppliedCorrectly ? (
                                <span className="text-xs text-green-600">QBO already applied {formatMoney(expectedAppliedCents)}</span>
                              ) : (
                                <span className="text-xs text-orange-600">
                                  QBO Applied: {qboAppliedCents != null ? formatMoney(qboAppliedCents) : '—'} · Expected {formatMoney(expectedAppliedCents)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {!invoiceQboId && (
                                <button
                                  onClick={() => syncInvoiceForCrawl(app.invoice_id)}
                                  disabled={syncingItems.has(`invoice-${app.invoice_id}`)}
                                  className="px-1 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                                >
                                  <IoDocumentText className="w-3 h-3" />
                                  {syncingItems.has(`invoice-${app.invoice_id}`) ? 'Syncing...' : 'Sync'}
                                </button>
                              )}
                              {shouldShowResync && !hideResync && (
                                <button
                                  onClick={() => syncInvoiceForCrawl(app.invoice_id)}
                                  disabled={syncingItems.has(`invoice-${app.invoice_id}`)}
                                  className="px-1 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-xs rounded hover:bg-orange-200 disabled:opacity-50 flex items-center gap-1"
                                >
                                  <IoRefresh className="w-3 h-3" />
                              {syncingItems.has(`invoice-${app.invoice_id}`) ? 'Syncing...' : 'Sync'}
                                </button>
                              )}
                              {shouldShowApplyButton && (
                                <button
                                  onClick={() => applyPaymentToInvoices()}
                                  disabled={
                                    syncingItems.has(`apply-payment-${currentPayment.payment_id}`) ||
                                    applyDisabledReason !== null
                                  }
                                  className="px-1 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs rounded hover:bg-indigo-200 disabled:opacity-50 flex items-center gap-1"
                                  title={
                                    applyDisabledReason === 'invoice_unsynced'
                                      ? 'Invoice must be synced to QBO first'
                                      : applyDisabledReason === 'payment_unsynced'
                                        ? 'Payment must be synced to QBO first'
                                        : applyDisabledReason === 'already_applied'
                                          ? 'Payment already applied in QBO'
                                          : applyDisabledReason === 'cannot_correct'
                                            ? 'This payment cannot be applied from the crawler'
                                            : ''
                                  }
                                >
                                  <IoArrowForward className="w-3 h-3" />
                                  {syncingItems.has(`apply-payment-${currentPayment.payment_id}`) ? 'Applying...' : 'Apply'}
                                </button>
                              )}
                            </div>
                          </div>
                        );})}
                      </div>
                    </div>
                  </div>

                  {/* QBO Payment Data */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <h4 className="font-medium text-gray-900">QBO Payment</h4>
                      </div>
                      <button
                        onClick={() => refreshCurrentPaymentWithQbo(currentPayment?.payment_id)}
                        disabled={qboLoading}
                        className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-md flex items-center gap-1"
                      >
                        {qboLoading ? (
                          <>
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                            Loading...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh QBO Data
                          </>
                        )}
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">QBO ID:</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${currentPaymentQboIdBadgeClass}`}>
                            {normalizedRemotePaymentQboId ?? (normalizedLocalPaymentQboId ? 'Not found' : 'Not synced')}
                          </span>
                          {!normalizedRemotePaymentQboId && normalizedLocalPaymentQboId && (
                            <span className="text-xs text-gray-500">Expected {normalizedLocalPaymentQboId}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">
                          {currentPaymentQboAmountCents != null ? formatMoney(currentPaymentQboAmountCents) : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer:</span>
                        <span className="font-medium">{currentPaymentQboCustomerName ?? '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer ID:</span>
                        <span className={`font-medium ${currentPaymentQboCustomerBadgeClass}`}>
                          {qboCustomerIdFromData ?? '—'}
                          {customerMatchState === 'match_inactive' && ' (Inactive)'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Date:</span>
                        <span className="font-medium">
                          {currentPaymentQboDate ? new Date(currentPaymentQboDate).toLocaleDateString() : '—'}
                        </span>
                      </div>
                    </div>

                    {/* QBO Invoice Applications */}
                    <div className="mt-4">
                      <h5 className="font-medium text-gray-900 mb-2">Applied to QBO Invoices:</h5>
                      <div className="space-y-2">
                        {currentPaymentApplications.map(({ app, invoiceQboId, qboAppliedCents, expectedAppliedCents, invoiceAppliedCorrectly }, index) => (
                          <div key={`${app.invoice_id}-${invoiceQboId ?? 'qbo'}-${index}`} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                            <div className="flex flex-col">
                              <span className="text-gray-600">
                                <span className={currentPaymentQboInvoiceBadgeClass}>
                                  {invoiceQboId ? `QBO Invoice #${invoiceQboId}` : 'QBO Invoice Not Synced'}
                                  {invoiceMatchState === 'match_inactive' && ' (Inactive)'}
                                </span>
                                {app.invoice_id && ` / EServ: ${app.invoice_id}`}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                QBO Applied: {qboAppliedCents != null ? formatMoney(qboAppliedCents) : '—'}
                              </span>
                              {invoiceAppliedCorrectly ? (
                                <span className="text-xs text-green-600">Amounts match</span>
                              ) : (
                                <span className="text-xs text-orange-600">Expected {formatMoney(expectedAppliedCents)}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {currentPayment && (
                  <div className="mt-6 flex justify-center gap-3 flex-wrap">
                    {/* Fix All Button - Complete Workflow */}
                    {currentPayment && (
                      (!currentPayment.customer_qbo_id && currentPayment.fk_bcustomer_id) ||
                      !currentPayment.payment_qbo_id ||
                      (currentPayment.applications || []).some(app => !app.invoice_qbo_id)
                    ) && (
                      <button
                        onClick={fixAllCurrentPaymentComplete}
                        disabled={isCrawlOverlayActive || syncingItems.has('fix-all')}
                        className="px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-md hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <IoRefresh className="w-4 h-4" />
                        {syncingItems.has('fix-all') ? 'Fixing All...' : 'Fix All'}
                      </button>
                    )}
                    <button
                      onClick={() => markPaymentAsProcessed(false)}
                      disabled={isCrawlOverlayActive}
                      className="px-2 py-1 bg-green-100 text-green-700 border border-green-200 text-sm font-medium rounded-md hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <IoCheckmark className="w-4 h-4" />
                      Mark as Processed
                    </button>
                    <button
                      onClick={markPaymentAsNeedsFix}
                      disabled={isCrawlOverlayActive}
                      className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 text-sm font-medium rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <IoRefresh className="w-4 h-4" />
                      Mark as Needs Fix
                    </button>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
                  <button
                    type="button"
                    onClick={goToPreviousPayment}
                    disabled={isCrawlOverlayActive || currentPage <= 1}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1 font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <IoArrowBack className="h-4 w-4" />
                    Previous
                  </button>
                  <span>
                    Viewing {currentPage > 0 ? currentPage : '—'}
                    {crawlProgress?.total ? ` of ${crawlProgress.total}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={goToNextPaymentManually}
                    disabled={isCrawlOverlayActive || (crawlProgress?.total ? currentPage >= crawlProgress.total : false)}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1 font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                    <IoArrowForward className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Status Messages */}
            {crawlStatus === 'idle' && !crawlTotals && (
              <div className="text-center py-8">
                <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Start</h3>
                <p className="text-gray-600 mb-4">Click "Fetch QBO Data" to get started with the reconciliation crawl.</p>
              </div>
            )}

            {crawlStatus === 'completed' && (
              <div className="text-center py-8">
                <div className="mx-auto h-12 w-12 text-green-400 mb-4">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Crawl Completed!</h3>
                <p className="text-gray-600">All payments have been processed. Check the results above.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppliedPaymentsScanModal;
